<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import BreezeAuthenticatedLayout from '@/Layouts/Authenticated.vue';
import { Head, Link, useForm } from '@inertiajs/inertia-vue3';
import { Inertia } from '@inertiajs/inertia';
import { Icon } from '@iconify/vue';
import axios from 'axios';
import * as XLSX from 'xlsx';

const props = defineProps({
    course: {
        type: Object,
        required: true
    },
    ciclo: {
        type: Object,
        required: true
    },
    students: {
        type: Array,
        default: () => []
    },
    topics: {
        type: Array,
        default: () => []
    },
    forums: {
        type: Array,
        default: () => []
    },
    forumMessages: {
        type: Array,
        default: () => []
    },
    attendance: {
        type: Array,
        default: () => []
    },
    grades: {
        type: Array,
        default: () => []
    },
    canWrite: {
        type: Boolean,
        default: false
    },
    canSetupRead: {
        type: Boolean,
        default: false
    },
    canSetupWrite: {
        type: Boolean,
        default: false
    },
    canFinanzasRead: {
        type: Boolean,
        default: false
    },
    canFinanzasReadAll: {
        type: Boolean,
        default: false
    },
    canFinanzasWrite: {
        type: Boolean,
        default: false
    },
    financeData: {
        type: Object,
        default: () => ({
            orders: [],
            stats: {},
            debtors: [],
            periods_summary: []
        })
    }
});

// Active tab management: 'contenido', 'alumnos', 'asistencia', 'foros', 'calificaciones', 'financiera', 'configuracion'
const activeTab = ref('contenido');
const subTabConfig = ref('general'); // 'general' | 'criterios'

// Modals State
const showTopicModal = ref(false);
const showTaskModal = ref(false);
const showForumModal = ref(false);
const showEvaluationModal = ref(false);
const showAlertModal = ref(false);
const currentTopicForTask = ref(null);
const activeForumId = ref(null);
const activeEvaluationRecord = ref(null);
const alertModalTitle = ref('');
const alertModalMessage = ref('');

const triggerAlert = (title, msg) => {
    alertModalTitle.value = title;
    alertModalMessage.value = msg;
    showAlertModal.value = true;
};

const isGradeInvalid = (gradeVal, maxPoints) => {
    if (gradeVal === '' || gradeVal === null || gradeVal === undefined) return false;
    const val = parseFloat(gradeVal);
    return isNaN(val) || val < 0 || val > maxPoints;
};

// --- Evaluation Criteria Calculation Helpers ---
const computeMaxPointsFromCriteria = (criteria, calculationType = 'promedio') => {
    if (!criteria || criteria.length === 0) return 20;
    if (calculationType === 'suma') {
        const total = criteria.reduce((sum, c) => sum + (parseFloat(c.max_score) || 0), 0);
        return Math.round(total * 100) / 100;
    } else {
        // Promedio ponderado de los puntajes máximos
        let totalWeighted = 0;
        let totalWeight = 0;
        criteria.forEach(c => {
            const w = parseFloat(c.weight) || 1;
            const max = parseFloat(c.max_score) || 20;
            totalWeighted += (max * w);
            totalWeight += w;
        });
        return totalWeight > 0 ? Math.round((totalWeighted / totalWeight) * 100) / 100 : 20;
    }
};

const calculateGradeFromCriteria = (criteria, criteriaGrades, calculationType = 'promedio') => {
    if (!criteria || criteria.length === 0 || !criteriaGrades) return '';
    let hasAny = false;
    let totalWeighted = 0;
    let totalWeight = 0;
    let sumScore = 0;

    criteria.forEach(c => {
        const val = criteriaGrades[c.id];
        if (val !== '' && val !== null && val !== undefined) {
            hasAny = true;
            const num = parseFloat(val) || 0;
            const w = parseFloat(c.weight) || 1;
            totalWeighted += (num * w);
            totalWeight += w;
            sumScore += num;
        }
    });

    if (!hasAny) return '';
    if (calculationType === 'suma') {
        return Math.round(sumScore * 100) / 100;
    } else {
        return totalWeight > 0 ? Math.round((totalWeighted / totalWeight) * 100) / 100 : 0;
    }
};

// Edit mode flags
const isEditingTopic = ref(false);
const isEditingTask = ref(false);
const isEditingForum = ref(false);

// Forms
const topicForm = useForm({
    id: null,
    id_course: props.course.id,
    id_ciclo: props.ciclo.id,
    week_number: 1,
    start_date: '',
    end_date: '',
    sort_order: 1,
    title: '',
    description: '',
    resources: [], // List of resource objects: { name, type, value }
    status: 0
});

const taskForm = useForm({
    id: null,
    id_topic: null,
    title: '',
    type: 'examen', // 'tarea', 'examen', 'practica'
    description: '',
    due_date: '',
    max_points: 20,
    evaluation_criteria: [],
    calculation_type: 'promedio'
});

const forumForm = useForm({
    id: null,
    id_course: props.course.id,
    title: '',
    description: '',
    start_date: '',
    end_date: ''
});

const forumMessageForm = useForm({
    id_forum: null,
    id_parent: null,
    message: ''
});

// Criteria Configuration Form
const defaultCriteriaConfig = {
    calculation_type: 'promedio', // 'promedio' | 'suma'
    applied_types: ['examen'], // ['examen', 'tarea', 'practica']
    criteria: [
        { id: 'crit_listening', name: 'Listening', weight: 1, max_score: 20 },
        { id: 'crit_reading', name: 'Reading', weight: 1, max_score: 20 },
        { id: 'crit_writing', name: 'Writing', weight: 1, max_score: 20 },
        { id: 'crit_speaking', name: 'Speaking', weight: 1, max_score: 20 }
    ]
};

const criteriaConfigForm = useForm({
    id_course: props.course.id,
    evaluation_criteria: props.course.evaluation_criteria && props.course.evaluation_criteria.criteria
        ? JSON.parse(JSON.stringify(props.course.evaluation_criteria))
        : JSON.parse(JSON.stringify(defaultCriteriaConfig))
});

const addCriterion = () => {
    if (!criteriaConfigForm.evaluation_criteria.criteria) {
        criteriaConfigForm.evaluation_criteria.criteria = [];
    }
    const idx = criteriaConfigForm.evaluation_criteria.criteria.length + 1;
    criteriaConfigForm.evaluation_criteria.criteria.push({
        id: 'crit_' + Date.now(),
        name: `Criterio ${idx}`,
        weight: 1,
        max_score: 20
    });
};

const removeCriterion = (index) => {
    criteriaConfigForm.evaluation_criteria.criteria.splice(index, 1);
};

const loadPresetLanguages = () => {
    criteriaConfigForm.evaluation_criteria = {
        calculation_type: 'promedio',
        applied_types: ['examen'],
        criteria: [
            { id: 'crit_listening', name: 'Listening', weight: 1, max_score: 20 },
            { id: 'crit_reading', name: 'Reading', weight: 1, max_score: 20 },
            { id: 'crit_writing', name: 'Writing', weight: 1, max_score: 20 },
            { id: 'crit_speaking', name: 'Speaking', weight: 1, max_score: 20 }
        ]
    };
};

const loadPresetStandard = () => {
    criteriaConfigForm.evaluation_criteria = {
        calculation_type: 'promedio',
        applied_types: ['examen'],
        criteria: [
            { id: 'crit_conceptual', name: 'Conceptual / Conocimiento', weight: 1, max_score: 20 },
            { id: 'crit_procedimental', name: 'Procedimental / Práctica', weight: 1, max_score: 20 },
            { id: 'crit_actitudinal', name: 'Actitudinal / Participación', weight: 1, max_score: 20 }
        ]
    };
};

const toggleAppliedType = (typeKey) => {
    const arr = criteriaConfigForm.evaluation_criteria.applied_types || [];
    const index = arr.indexOf(typeKey);
    if (index > -1) {
        arr.splice(index, 1);
    } else {
        arr.push(typeKey);
    }
    criteriaConfigForm.evaluation_criteria.applied_types = arr;
};

const submitCriteriaConfig = () => {
    criteriaConfigForm.post(route('independent-courses.criteria.save'), {
        preserveScroll: true,
        onSuccess: () => {
            triggerAlert('Guardado Exitoso', 'La configuración de criterios de evaluación ha sido guardada correctamente.');
        }
    });
};

// --- General Setup Logic (Fechas y Periodos del Curso) ---
const generalSetupForm = ref({
    id_course: props.course.id,
    start_date: props.course.start_date ? String(props.course.start_date).substring(0, 10) : '',
    end_date: props.course.end_date ? String(props.course.end_date).substring(0, 10) : '',
    periods: Array.isArray(props.course.periods) ? JSON.parse(JSON.stringify(props.course.periods)) : []
});

const isSavingGeneralSetup = ref(false);

const addPeriod = () => {
    const nextIdx = generalSetupForm.value.periods.length + 1;
    const year = props.course.anio || new Date().getFullYear();
    const periodCode = `${year}${String(nextIdx).padStart(2, '0')}`;
    const courseShort = props.course.label_short || props.course.label || 'Curso';
    generalSetupForm.value.periods.push({
        id: 'p_' + Date.now() + '_' + Math.random().toString(36).substr(2, 4),
        periodo: periodCode,
        label: `${courseShort} - Mes ${nextIdx}`,
        start_date: '',
        end_date: ''
    });
};

const removePeriod = (idx) => {
    generalSetupForm.value.periods.splice(idx, 1);
};

const generateSixDefaultPeriods = () => {
    const year = props.course.anio || new Date().getFullYear();
    const courseShort = props.course.label_short || props.course.label || 'Curso';
    const newPeriods = [];
    for (let i = 1; i <= 6; i++) {
        newPeriods.push({
            id: 'p_' + Date.now() + '_' + i,
            periodo: `${year}${String(i).padStart(2, '0')}`,
            label: `${courseShort} - Mes ${i}`,
            start_date: '',
            end_date: ''
        });
    }
    generalSetupForm.value.periods = newPeriods;
};

const saveGeneralSetup = async () => {
    if (!props.canSetupWrite) {
        triggerAlert('Sin Permisos', 'No tiene permisos para modificar la configuración del curso.');
        return;
    }
    isSavingGeneralSetup.value = true;
    try {
        await axios.post(route('independent-courses.general-setup.save'), generalSetupForm.value);
        triggerAlert('Guardado Exitoso', 'La configuración general del curso y sus periodos han sido guardados correctamente.');
        Inertia.reload({ only: ['course', 'financeData'] });
    } catch (err) {
        triggerAlert('Error', err.response?.data?.message || 'No se pudo guardar la configuración.');
    } finally {
        isSavingGeneralSetup.value = false;
    }
};

// --- Financial Tab State and Methods ---
const coursePriceInput = ref(props.course.price !== undefined && props.course.price !== null ? parseFloat(props.course.price) : 0);
const isEditingPrice = ref(false);
const isSavingPrice = ref(false);

watch(() => props.course.price, (newPrice) => {
    coursePriceInput.value = newPrice !== undefined && newPrice !== null ? parseFloat(newPrice) : 0;
});

const saveCoursePrice = async () => {
    if (!props.canFinanzasWrite) {
        triggerAlert('Sin Permisos', 'No tiene permisos para modificar el precio del curso.');
        return;
    }
    isSavingPrice.value = true;
    try {
        await axios.post(route('independent-courses.update-price'), {
            id_course: props.course.id,
            price: coursePriceInput.value
        });
        isEditingPrice.value = false;
        triggerAlert('Precio Actualizado', 'El precio del curso ha sido guardado correctamente.');
        Inertia.reload({ only: ['course', 'financeData'] });
    } catch (err) {
        triggerAlert('Error', err.response?.data?.message || 'No se pudo guardar el precio.');
    } finally {
        isSavingPrice.value = false;
    }
};

// Filtros y búsquedas en pestaña financiera
const financeOrderSearch = ref('');
const financeOrderStatus = ref('all');
const financeDebtorSearch = ref('');

const filteredCourseOrders = computed(() => {
    let list = props.financeData?.orders || [];
    if (financeOrderStatus.value !== 'all') {
        const st = parseInt(financeOrderStatus.value);
        list = list.filter(o => o.status === st);
    }
    if (financeOrderSearch.value.trim()) {
        const q = financeOrderSearch.value.toLowerCase();
        list = list.filter(o => 
            (o.ref && o.ref.toLowerCase().includes(q)) ||
            (o.label && o.label.toLowerCase().includes(q)) ||
            (o.student?.person?.apellidos && o.student.person.apellidos.toLowerCase().includes(q)) ||
            (o.student?.person?.nombres && o.student.person.nombres.toLowerCase().includes(q)) ||
            (o.student?.person?.num_doc && o.student.person.num_doc.includes(q))
        );
    }
    return list;
});

const filteredDebtors = computed(() => {
    let list = props.financeData?.debtors || [];
    if (financeDebtorSearch.value.trim()) {
        const q = financeDebtorSearch.value.toLowerCase();
        list = list.filter(d => 
            d.apellidos.toLowerCase().includes(q) ||
            d.nombres.toLowerCase().includes(q) ||
            d.num_doc.includes(q) ||
            (d.student_ref && d.student_ref.toLowerCase().includes(q))
        );
    }
    return list;
});

const exportFinanceToExcel = () => {
    const ordersList = props.financeData?.orders || [];
    const rows = ordersList.map((o, idx) => ({
        'Nº': idx + 1,
        'Ref Orden': o.ref,
        'Estudiante': `${o.student?.person?.apellidos || ''} ${o.student?.person?.nombres || ''}`,
        'Doc. Identidad': o.student?.person?.num_doc || '',
        'Periodo': o.periodo || '',
        'Concepto': o.label || '',
        'F. Emisión': o.fecha_emision || '',
        'F. Vencimiento': o.fecha_vencimiento || '',
        'Monto Total (S/)': props.canFinanzasReadAll ? parseFloat(o.total_amount) : '***',
        'Monto Pagado (S/)': props.canFinanzasReadAll ? parseFloat(o.paid_amount) : '***',
        'Saldo (S/)': props.canFinanzasReadAll ? parseFloat(o.balance) : '***',
        'Estado': o.status === 1 ? 'Pagado' : (o.status === 2 ? 'Pago Parcial' : 'Pendiente')
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Finanzas Curso');
    XLSX.writeFile(wb, `Reporte_Financiero_${props.course.ref || 'Curso'}.xlsx`);
};

// Resources temporary input
const newResourceName = ref('');
const newResourceType = ref('link');
const newResourceValue = ref('');
const fileInput = ref(null);
const isUploading = ref(false);

// Attendance Date
const attendanceDate = ref(new Date().toISOString().substring(0, 10));
const attendanceRecords = ref([]);

// Grades List Form
const gradesForm = useForm({
    grades: []
});

// Setup Attendance Records on date change or mount
const initAttendance = () => {
    attendanceRecords.value = props.students.map(student => {
        const existing = props.attendance.find(att => {
            const attDate = att.date.substring(0, 10);
            return att.id_student === student.id && attDate === attendanceDate.value;
        });

        return {
            id_student: student.id,
            apellidos: student.apellidos,
            nombres: student.nombres,
            num_doc: student.num_doc,
            ref: student.ref,
            status: existing ? existing.status : 'falta',
            observation: existing ? (existing.observation || '') : ''
        };
    });
};

const savedGrades = ref([]);
const isRefreshingGrades = ref(false);

const refreshGrades = () => {
    isRefreshingGrades.value = true;
    Inertia.reload({
        only: ['grades', 'topics', 'students'],
        preserveScroll: true,
        preserveState: true,
        onFinish: () => {
            isRefreshingGrades.value = false;
        }
    });
};

const hasUnsavedChanges = computed(() => {
    return JSON.stringify(gradesForm.grades) !== JSON.stringify(savedGrades.value);
});

// Setup Grades Data Structure
const initGradesForm = () => {
    const gradesList = [];
    props.students.forEach(student => {
        props.topics.forEach(topic => {
            (topic.tasks || []).forEach(task => {
                const existingGrade = props.grades.find(g => g.id_student === student.id && g.id_task === task.id);
                gradesList.push({
                    id_student: student.id,
                    id_task: task.id,
                    grade: existingGrade ? existingGrade.grade : '',
                    criteria_grades: existingGrade && existingGrade.criteria_grades ? JSON.parse(JSON.stringify(existingGrade.criteria_grades)) : {},
                    feedback: existingGrade ? (existingGrade.feedback || '') : '',
                    submission_text: existingGrade ? (existingGrade.submission_text || '') : '',
                    submission_file: existingGrade ? (existingGrade.submission_file || '') : '',
                    submitted_at: existingGrade ? (existingGrade.submitted_at || '') : '',
                    status: existingGrade ? (existingGrade.status || '') : '',
                    student_name: `${student.apellidos}, ${student.nombres}`,
                    task_title: task.title,
                    task_type: task.type,
                    max_points: task.max_points,
                    evaluation_criteria: task.evaluation_criteria || [],
                    calculation_type: task.calculation_type || 'promedio'
                });
            });
        });
    });
    gradesForm.grades = gradesList;
    savedGrades.value = JSON.parse(JSON.stringify(gradesList));
};

onMounted(() => {
    initAttendance();
    initGradesForm();
});

watch(attendanceDate, () => {
    initAttendance();
});

watch(() => props.grades, () => {
    initGradesForm();
}, { deep: true });

watch(() => props.topics, () => {
    initGradesForm();
}, { deep: true });

watch(() => props.course, (newCourse) => {
    if (newCourse?.evaluation_criteria) {
        criteriaConfigForm.evaluation_criteria = JSON.parse(JSON.stringify(newCourse.evaluation_criteria));
    }
}, { deep: true });

// Attendance functions
const saveAttendance = () => {
    const form = useForm({
        id_course: props.course.id,
        date: attendanceDate.value,
        records: attendanceRecords.value
    });

    form.post(route('independent-courses.attendance.save'), {
        onSuccess: () => {
            alert('Asistencia guardada correctamente.');
            initAttendance();
        }
    });
};

// Grades functions
const submitGradesForm = () => {
    // Validate all grades in gradesForm.grades
    const invalidGrade = gradesForm.grades.find(g => isGradeInvalid(g.grade, g.max_points));
    if (invalidGrade) {
        triggerAlert(
            'Puntaje no permitido',
            `El puntaje ingresado para ${invalidGrade.student_name} en "${invalidGrade.task_title}" (${invalidGrade.grade}) no es válido. Debe ser un valor entre 0 y ${invalidGrade.max_points} puntos.`
        );
        return;
    }

    gradesForm.post(route('independent-courses.grades.save'), {
        preserveScroll: true
    });
};

const openEvaluationModal = (studentId, taskId) => {
    const record = gradesForm.grades.find(g => g.id_student === studentId && g.id_task === taskId);
    if (record) {
        if (!record.criteria_grades || typeof record.criteria_grades !== 'object') {
            record.criteria_grades = {};
        }
        if (record.evaluation_criteria && record.evaluation_criteria.length > 0) {
            record.evaluation_criteria.forEach(crit => {
                if (record.criteria_grades[crit.id] === undefined) {
                    record.criteria_grades[crit.id] = '';
                }
            });
        }
        activeEvaluationRecord.value = record;
        showEvaluationModal.value = true;
    }
};

const onCriterionGradeChange = () => {
    if (!activeEvaluationRecord.value) return;
    const record = activeEvaluationRecord.value;
    if (!record.evaluation_criteria || record.evaluation_criteria.length === 0) return;

    const computedGrade = calculateGradeFromCriteria(
        record.evaluation_criteria,
        record.criteria_grades,
        record.calculation_type || 'promedio'
    );
    record.grade = computedGrade;
};

const submitSingleGrade = () => {
    if (!activeEvaluationRecord.value) return;
    
    const record = activeEvaluationRecord.value;
    
    if (isGradeInvalid(record.grade, record.max_points)) {
        triggerAlert(
            'Puntaje no permitido',
            `El puntaje ingresado (${record.grade}) no es válido. Debe ser un valor mayor o igual a 0 y menor o igual al puntaje máximo permitido de ${record.max_points} puntos.`
        );
        return;
    }

    // Validate each criterion grade
    if (record.evaluation_criteria && record.evaluation_criteria.length > 0) {
        for (const crit of record.evaluation_criteria) {
            const val = record.criteria_grades[crit.id];
            if (val !== '' && val !== null && val !== undefined) {
                const num = parseFloat(val);
                if (isNaN(num) || num < 0 || num > crit.max_score) {
                    triggerAlert(
                        'Puntaje de Criterio no permitido',
                        `El puntaje ingresado para el criterio "${crit.name}" (${val}) no es válido. Debe ser un valor entre 0 y ${crit.max_score} puntos.`
                    );
                    return;
                }
            }
        }
    }
    
    const singleForm = useForm({
        grades: [{
            id_student: record.id_student,
            id_task: record.id_task,
            grade: record.grade,
            criteria_grades: record.criteria_grades,
            feedback: record.feedback,
            status: record.status
        }]
    });
    
    singleForm.post(route('independent-courses.grades.save'), {
        preserveScroll: true,
        onSuccess: () => {
            showEvaluationModal.value = false;
            activeEvaluationRecord.value = null;
            triggerAlert('Guardado Exitoso', 'La calificación y retroalimentación han sido guardadas correctamente.');
        }
    });
};

// Topic CRUD actions
const openCreateTopicModal = () => {
    isEditingTopic.value = false;
    topicForm.reset();
    topicForm.id = null;
    topicForm.week_number = props.topics.length + 1;
    topicForm.sort_order = props.topics.length + 1;
    topicForm.resources = [];
    topicForm.status = 0;
    showTopicModal.value = true;
};

const openEditTopicModal = (topic) => {
    isEditingTopic.value = true;
    topicForm.id = topic.id;
    topicForm.week_number = topic.week_number;
    topicForm.start_date = topic.start_date ? topic.start_date.substring(0, 10) : '';
    topicForm.end_date = topic.end_date ? topic.end_date.substring(0, 10) : '';
    topicForm.sort_order = topic.sort_order;
    topicForm.title = topic.title;
    topicForm.description = topic.description || '';
    topicForm.resources = Array.isArray(topic.resources) ? [...topic.resources] : [];
    topicForm.status = topic.status !== undefined ? topic.status : 0;
    showTopicModal.value = true;
};

const submitTopicForm = () => {
    if (isEditingTopic.value) {
        topicForm.put(route('independent-courses.topic.update', topicForm.id), {
            onSuccess: () => showTopicModal.value = false
        });
    } else {
        topicForm.post(route('independent-courses.topic.store'), {
            onSuccess: () => showTopicModal.value = false
        });
    }
};

const deleteTopic = (topic) => {
    // Check for child elements (tasks or study resources)
    const hasTasks = topic.tasks && topic.tasks.length > 0;
    const hasResources = topic.resources && topic.resources.length > 0;

    if (hasTasks || hasResources) {
        alert('No se puede eliminar este tema porque contiene evaluaciones o recursos de estudio asociados. Primero elimine estos elementos hijos por seguridad.');
        return;
    }

    if (confirm('¿Está seguro de que desea eliminar este tema?')) {
        const f = useForm({});
        f.delete(route('independent-courses.topic.destroy', topic.id));
    }
};

// Resource Upload
const addResource = () => {
    if (!newResourceName.value) return;

    if (newResourceType.value === 'link' && !newResourceValue.value) return;

    if (newResourceType.value === 'link') {
        topicForm.resources.push({
            name: newResourceName.value,
            type: 'link',
            value: newResourceValue.value
        });
        newResourceName.value = '';
        newResourceValue.value = '';
    }
};

const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    isUploading.value = true;
    const formData = new FormData();
    formData.append('file', file);

    try {
        const response = await axios.post(route('independent-courses.topic.upload-resource'), formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });

        if (response.data.success) {
            topicForm.resources.push({
                name: newResourceName.value || response.data.name,
                type: 'pdf',
                value: response.data.url
            });
            newResourceName.value = '';
        } else {
            alert('Error al subir archivo');
        }
    } catch (e) {
        console.error(e);
        alert('Error al subir archivo');
    } finally {
        isUploading.value = false;
        if (fileInput.value) fileInput.value.value = '';
    }
};

const removeResource = (index) => {
    topicForm.resources.splice(index, 1);
};

// Task CRUD actions
const onTaskTypeChange = () => {
    const courseCriteria = props.course.evaluation_criteria;
    const appliedTypes = courseCriteria?.applied_types || ['examen'];
    if (appliedTypes.includes(taskForm.type) && courseCriteria?.criteria?.length > 0) {
        taskForm.evaluation_criteria = JSON.parse(JSON.stringify(courseCriteria.criteria));
        taskForm.calculation_type = courseCriteria.calculation_type || 'promedio';
        onCriterionMaxScoreChange();
    } else {
        taskForm.evaluation_criteria = [];
        taskForm.calculation_type = 'promedio';
        if (!taskForm.max_points) taskForm.max_points = 20;
    }
};

const onCriterionMaxScoreChange = () => {
    if (!taskForm.evaluation_criteria || taskForm.evaluation_criteria.length === 0) return;
    taskForm.max_points = computeMaxPointsFromCriteria(taskForm.evaluation_criteria, taskForm.calculation_type || 'promedio');
};

const removeTaskCriterion = (index) => {
    taskForm.evaluation_criteria.splice(index, 1);
    if (taskForm.evaluation_criteria && taskForm.evaluation_criteria.length > 0) {
        onCriterionMaxScoreChange();
    } else {
        if (!taskForm.max_points || taskForm.max_points <= 0) {
            taskForm.max_points = 20;
        }
    }
};

const addCourseCriterionToTask = (criterion) => {
    if (!Array.isArray(taskForm.evaluation_criteria)) {
        taskForm.evaluation_criteria = [];
    }
    taskForm.evaluation_criteria.push(JSON.parse(JSON.stringify(criterion)));
    onCriterionMaxScoreChange();
};

const resetTaskCriteriaToCourse = () => {
    const courseCriteria = props.course?.evaluation_criteria;
    if (courseCriteria?.criteria && courseCriteria.criteria.length > 0) {
        taskForm.evaluation_criteria = JSON.parse(JSON.stringify(courseCriteria.criteria));
        taskForm.calculation_type = courseCriteria.calculation_type || 'promedio';
        onCriterionMaxScoreChange();
    }
};

const availableCourseCriteria = computed(() => {
    const courseCriteria = props.course?.evaluation_criteria?.criteria || [];
    if (!taskForm.evaluation_criteria || !Array.isArray(taskForm.evaluation_criteria)) {
        return courseCriteria;
    }
    const currentIds = taskForm.evaluation_criteria.map(c => c.id);
    return courseCriteria.filter(c => !currentIds.includes(c.id));
});

const openCreateTaskModal = (topic) => {
    isEditingTask.value = false;
    currentTopicForTask.value = topic;
    taskForm.reset();
    taskForm.id_topic = topic.id;
    taskForm.type = 'tarea';
    onTaskTypeChange();
    showTaskModal.value = true;
};

const openEditTaskModal = (task, topic) => {
    isEditingTask.value = true;
    currentTopicForTask.value = topic;
    taskForm.id = task.id;
    taskForm.id_topic = task.id_topic;
    taskForm.title = task.title;
    taskForm.type = task.type;
    taskForm.description = task.description || '';
    taskForm.due_date = task.due_date ? task.due_date.replace(' ', 'T') : '';
    taskForm.max_points = task.max_points;
    taskForm.evaluation_criteria = Array.isArray(task.evaluation_criteria) ? JSON.parse(JSON.stringify(task.evaluation_criteria)) : [];
    taskForm.calculation_type = task.calculation_type || props.course.evaluation_criteria?.calculation_type || 'promedio';
    showTaskModal.value = true;
};

const submitTaskForm = () => {
    if (isEditingTask.value) {
        taskForm.put(route('independent-courses.task.update', taskForm.id), {
            onSuccess: () => showTaskModal.value = false
        });
    } else {
        taskForm.post(route('independent-courses.task.store'), {
            onSuccess: () => showTaskModal.value = false
        });
    }
};

const deleteTask = (taskId) => {
    if (confirm('¿Está seguro de que desea eliminar esta evaluación? Se borrarán todas las notas asignadas.')) {
        const f = useForm({});
        f.delete(route('independent-courses.task.destroy', taskId));
    }
};

// Forums CRUD actions
const openCreateForumModal = () => {
    isEditingForum.value = false;
    forumForm.reset();
    forumForm.id = null;
    showForumModal.value = true;
};

const openEditForumModal = (forum) => {
    isEditingForum.value = true;
    forumForm.id = forum.id;
    forumForm.id_course = props.course.id;
    forumForm.title = forum.title;
    forumForm.description = forum.description || '';
    forumForm.start_date = forum.start_date ? forum.start_date.replace(' ', 'T').substring(0, 16) : '';
    forumForm.end_date = forum.end_date ? forum.end_date.replace(' ', 'T').substring(0, 16) : '';
    showForumModal.value = true;
};

const submitForumForm = () => {
    if (isEditingForum.value) {
        forumForm.put(route('independent-courses.forum.update', forumForm.id), {
            onSuccess: () => showForumModal.value = false
        });
    } else {
        forumForm.post(route('independent-courses.forum.store'), {
            onSuccess: () => {
                showForumModal.value = false;
                forumForm.reset();
            }
        });
    }
};

const deleteForum = (forumId) => {
    const messageCount = getForumMessageCount(forumId);
    if (messageCount > 0) {
        alert('No es posible eliminar el foro pues ya existen comentarios en él.');
        return;
    }

    if (confirm('¿Está seguro de que desea eliminar este foro?')) {
        const f = useForm({});
        f.delete(route('independent-courses.forum.destroy', forumId));
    }
};

const openForumThread = (forumId) => {
    activeForumId.value = forumId;
    forumMessageForm.id_forum = forumId;
    forumMessageForm.id_parent = null;
    forumMessageForm.message = '';
};

const sendForumMessage = () => {
    if (!forumMessageForm.message) return;

    forumMessageForm.post(route('independent-courses.forum.message.store'), {
        onSuccess: () => {
            forumMessageForm.message = '';
        }
    });
};

const getForumMessageCount = (forumId) => {
    return props.forumMessages.filter(msg => msg.id_forum === forumId).length;
};

const getForumThreadMessages = (forumId) => {
    return props.forumMessages.filter(msg => msg.id_forum === forumId);
};

const isForumActive = (forum) => {
    const now = new Date();
    const start = new Date(forum.start_date);
    const end = new Date(forum.end_date);
    return now >= start && now <= end;
};

// Helper: formatDate
const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
            const parts = dateStr.split('-');
            return `${parts[2]}/${parts[1]}/${parts[0]}`;
        }
        const localDateStr = dateStr.includes('T') ? dateStr : dateStr.replace(/-/g, '/');
        const date = new Date(localDateStr);
        return date.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch (e) {
        return dateStr;
    }
};

const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
        const localDateStr = dateStr.includes('T') ? dateStr : dateStr.replace(/-/g, '/');
        const date = new Date(localDateStr);
        return date.toLocaleDateString('es-PE', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    } catch (e) {
        return dateStr;
    }
};

// Computed for Gradebook Task columns
const gradebookTasks = computed(() => {
    const tasks = [];
    props.topics.forEach(topic => {
        (topic.tasks || []).forEach(task => {
            tasks.push(task);
        });
    });
    return tasks;
});

const getGradeRecord = (studentId, taskId) => {
    return gradesForm.grades.find(g => g.id_student === studentId && g.id_task === taskId);
};

const exportGradesToExcel = () => {
    if (hasUnsavedChanges.value) return;

    const courseTitle = props.course.label || 'Curso';
    const cicloLabel = props.ciclo.label || '';

    // Title rows
    const data = [
        [`CALIFICACIONES - ${courseTitle.toUpperCase()}`],
        [`Ciclo: ${cicloLabel}`],
        [], // Empty separator
    ];

    // Build header row
    const headerRow = ['Estudiante'];
    gradebookTasks.value.forEach(task => {
        headerRow.push(`${task.title} (${task.max_points} pts)`);
    });
    data.push(headerRow);

    // Build student rows
    props.students.forEach(student => {
        const rowData = [`${student.apellidos}, ${student.nombres}`];
        gradebookTasks.value.forEach(task => {
            const gradeRecord = getGradeRecord(student.id, task.id);
            const gradeVal = gradeRecord && (gradeRecord.grade !== '' && gradeRecord.grade !== null && gradeRecord.grade !== undefined)
                ? parseFloat(gradeRecord.grade)
                : '-';
            rowData.push(gradeVal);
        });
        data.push(rowData);
    });

    // Generate sheet & workbook
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(data);

    // Auto-calculate column widths
    const colWidths = [];
    data.forEach(row => {
        row.forEach((val, colIndex) => {
            const strVal = val ? val.toString() : '';
            colWidths[colIndex] = Math.max(colWidths[colIndex] || 8, strVal.length + 3);
        });
    });
    ws['!cols'] = colWidths.map(w => ({ wch: w }));

    XLSX.utils.book_append_sheet(wb, ws, "Calificaciones");

    // Write file
    const sanitizedTitle = courseTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase();
    XLSX.writeFile(wb, `calificaciones_${sanitizedTitle}.xlsx`);
};
</script>

<template>
    <Head :title="`Ficha: ${course.label}`" />

    <BreezeAuthenticatedLayout>
        <div>
            <!-- Navigation Header Card -->
            <div class="bg-white dark:bg-slate-900 rounded-3xl border border-slate-100 dark:border-slate-800 shadow-sm p-6 transition-all duration-300">
                <div class="flex flex-col md:flex-row md:items-start justify-between gap-4">
                    <div class="flex items-start gap-4">
                        <Link :href="route('independent-courses.index')" 
                              class="p-3 bg-slate-50 hover:bg-slate-100 dark:bg-slate-800/80 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400 rounded-2xl transition-all duration-200 shrink-0 hover:scale-105"
                              title="Volver a Cursos Libres">
                            <Icon icon="tabler:arrow-left" class="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                        </Link>
                        <div class="space-y-1">
                            <div class="flex items-center gap-2 flex-wrap">
                                <span class="text-[9px] font-bold font-mono px-2 py-0.5 bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400 rounded uppercase">
                                    {{ course.ref || 'Curso Libre' }}
                                </span>
                                <span v-if="course.credits" class="text-[9px] font-bold font-mono px-2 py-0.5 bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400 rounded uppercase">
                                    {{ course.credits }} Créditos
                                </span>
                            </div>
                            <h1 class="text-xl font-bold text-slate-800 dark:text-white mt-1">{{ course.label }}</h1>
                            <div class="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 dark:text-slate-400 font-medium pt-0.5">
                                <span class="flex items-center gap-1">
                                    <Icon icon="tabler:calendar" class="w-4 h-4 text-slate-400" />
                                    Año: {{ course.anio }}
                                </span>
                                <span class="w-1 h-1 bg-slate-300 dark:bg-slate-700 rounded-full hidden sm:inline-block"></span>
                                <span class="flex items-center gap-1">
                                    <Icon icon="tabler:users" class="w-4 h-4 text-slate-400" />
                                    Matriculados: {{ students.length }}
                                </span>
                                <span v-if="course.description" class="w-1 h-1 bg-slate-300 dark:bg-slate-700 rounded-full hidden md:inline-block"></span>
                                <span v-if="course.description" class="text-slate-400 dark:text-slate-500 font-normal">
                                    {{ course.description }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Tab Menu (Horizontal menu design system) -->
                <div class="mt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div class="overflow-x-auto -mx-6 px-6 sm:mx-0 sm:px-0">
                        <div class="flex items-center gap-1.5 p-1.5 bg-slate-50 dark:bg-slate-950/40 rounded-2xl border border-slate-100 dark:border-slate-800/80 w-max">
                            <button @click="activeTab = 'contenido'"
                                    :class="[activeTab === 'contenido' ? 'bg-white dark:bg-slate-850 text-indigo-600 dark:text-indigo-400 shadow-sm font-semibold' : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200']"
                                    class="flex items-center gap-2 px-4 py-2 rounded-xl text-xs transition-all duration-200">
                                <Icon icon="tabler:book" class="w-4 h-4" />
                                Contenido del curso
                            </button>
                            <button @click="activeTab = 'alumnos'"
                                    :class="[activeTab === 'alumnos' ? 'bg-white dark:bg-slate-850 text-indigo-600 dark:text-indigo-400 shadow-sm font-semibold' : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200']"
                                    class="flex items-center gap-2 px-4 py-2 rounded-xl text-xs transition-all duration-200">
                                <Icon icon="tabler:users" class="w-4 h-4" />
                                Alumnos
                            </button>
                            <button @click="activeTab = 'asistencia'"
                                    :class="[activeTab === 'asistencia' ? 'bg-white dark:bg-slate-850 text-indigo-600 dark:text-indigo-400 shadow-sm font-semibold' : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200']"
                                    class="flex items-center gap-2 px-4 py-2 rounded-xl text-xs transition-all duration-200">
                                <Icon icon="tabler:clipboard-check" class="w-4 h-4" />
                                Asistencia
                            </button>
                            <button @click="activeTab = 'foros'"
                                    :class="[activeTab === 'foros' ? 'bg-white dark:bg-slate-850 text-indigo-600 dark:text-indigo-400 shadow-sm font-semibold' : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200']"
                                    class="flex items-center gap-2 px-4 py-2 rounded-xl text-xs transition-all duration-200">
                                <Icon icon="tabler:messages" class="w-4 h-4" />
                                Foros
                            </button>
                            <button @click="activeTab = 'calificaciones'"
                                    :class="[activeTab === 'calificaciones' ? 'bg-white dark:bg-slate-850 text-indigo-600 dark:text-indigo-400 shadow-sm font-semibold' : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200']"
                                    class="flex items-center gap-2 px-4 py-2 rounded-xl text-xs transition-all duration-200">
                                <Icon icon="tabler:award" class="w-4 h-4" />
                                Calificaciones
                            </button>
                            <button v-if="canFinanzasRead" @click="activeTab = 'financiera'"
                                    :class="[activeTab === 'financiera' ? 'bg-white dark:bg-slate-850 text-indigo-600 dark:text-indigo-400 shadow-sm font-semibold' : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200']"
                                    class="flex items-center gap-2 px-4 py-2 rounded-xl text-xs transition-all duration-200">
                                <Icon icon="tabler:wallet" class="w-4 h-4" />
                                Financiera
                            </button>
                            <button v-if="canSetupRead" @click="activeTab = 'configuracion'"
                                    :class="[activeTab === 'configuracion' ? 'bg-white dark:bg-slate-850 text-indigo-600 dark:text-indigo-400 shadow-sm font-semibold' : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200']"
                                    class="flex items-center gap-2 px-4 py-2 rounded-xl text-xs transition-all duration-200">
                                <Icon icon="tabler:settings" class="w-4 h-4" />
                                Configuración
                            </button>
                        </div>
                    </div>

                    <!-- Right Side Action Button inside tab menu container -->
                    <div v-if="activeTab === 'contenido' && canWrite" class="shrink-0">
                        <button @click="openCreateTopicModal"
                                class="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-semibold shadow-sm transition-all duration-200 hover:-translate-y-0.5">
                            <Icon icon="tabler:plus" class="w-4 h-4" />
                            Crear Tema de la Semana
                        </button>
                    </div>
                </div>
            </div>

            <!-- Content Area -->
            <div class="space-y-6 mt-6">

                <!-- TAB 1: CONTENIDO -->
                <div v-if="activeTab === 'contenido'" class="space-y-6">
                    <div class="flex items-center justify-between">
                        <h2 class="text-base font-bold text-slate-800 dark:text-white uppercase tracking-wider flex items-center gap-2">
                            <Icon icon="tabler:timeline" class="w-5 h-5 text-indigo-500" />
                            Curricula & Ruta de Aprendizaje
                        </h2>
                    </div>

                    <!-- Visual Learning Path / Timeline -->
                    <div v-if="topics.length === 0" class="text-center py-16 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800/80 rounded-3xl shadow-sm">
                        <Icon icon="tabler:books-off" class="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto" />
                        <h4 class="text-sm font-semibold text-slate-700 dark:text-slate-300 mt-4">Aún no se ha estructurado la curricula</h4>
                        <p class="text-xs text-slate-400 mt-1 max-w-sm mx-auto">Agrega temas, asocia fechas, y sube recursos o crea tareas/exámenes semanales.</p>
                        <button v-if="canWrite" @click="openCreateTopicModal"
                                class="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-semibold transition-all">
                            Crear Primer Tema
                        </button>
                    </div>

                    <div v-else class="relative border-l-2 border-slate-100 dark:border-slate-800 ml-4 md:ml-8 space-y-8 py-2">
                        <div v-for="(topic, index) in topics" :key="topic.id" class="relative pl-6 md:pl-10">
                            <!-- Bullet Icon on Timeline -->
                            <div class="absolute -left-[11px] top-1 w-5 h-5 rounded-full border-2 border-white dark:border-slate-900 flex items-center justify-center bg-indigo-600 shadow-md">
                                <span class="text-[9px] font-bold text-white">{{ topic.week_number }}</span>
                            </div>

                            <!-- Topic Card Card -->
                            <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 shadow-sm rounded-2xl p-6 transition-all hover:shadow-md">
                                <div class="flex flex-col md:flex-row md:items-start justify-between gap-4">
                                    <div class="space-y-1">
                                        <div class="flex flex-wrap items-center gap-2">
                                            <span class="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 rounded">
                                                Semana {{ topic.week_number }}
                                            </span>
                                            <span :class="[
                                                topic.status === 1
                                                    ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400'
                                                    : 'bg-amber-50 text-amber-600 dark:bg-amber-950/40 dark:text-amber-400'
                                            ]" class="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded">
                                                {{ topic.status === 1 ? 'Validado' : 'Borrador' }}
                                            </span>
                                            <span v-if="topic.start_date || topic.end_date" class="text-[10px] text-slate-400 font-medium">
                                                ({{ formatDate(topic.start_date) }} - {{ formatDate(topic.end_date) }})
                                            </span>
                                        </div>
                                        <h3 class="text-base font-bold text-slate-800 dark:text-white">{{ topic.title }}</h3>
                                        <p class="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">{{ topic.description }}</p>
                                    </div>

                                    <!-- Actions on Topic -->
                                    <div v-if="canWrite" class="flex items-center gap-1.5 shrink-0 self-end md:self-start">
                                        <button @click="openCreateTaskModal(topic)"
                                                class="px-2.5 py-1.5 text-[10px] font-semibold bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 rounded-lg hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-all flex items-center gap-1"
                                                title="Añadir Evaluación">
                                            <Icon icon="tabler:clipboard-plus" class="w-3.5 h-3.5" />
                                            Evaluar
                                        </button>
                                        <button @click="openEditTopicModal(topic)"
                                                class="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg transition-all"
                                                title="Editar Tema">
                                            <Icon icon="tabler:edit" class="w-4 h-4" />
                                        </button>
                                        <button @click="deleteTopic(topic)"
                                                class="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg transition-all"
                                                title="Eliminar Tema">
                                            <Icon icon="tabler:trash" class="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>

                                <!-- Resources & Tasks Section Inside Card -->
                                <div class="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6 pt-6 border-t border-slate-50 dark:border-slate-800/60">
                                    
                                    <!-- Resources -->
                                    <div class="space-y-3">
                                        <h4 class="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
                                            <Icon icon="tabler:folders" class="w-4 h-4 text-slate-400" />
                                            Recursos de Estudio
                                        </h4>
                                        <ul v-if="topic.resources && topic.resources.length" class="space-y-2">
                                            <li v-for="(res, idx) in topic.resources" :key="idx"
                                                class="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-950/20 rounded-xl border border-slate-100 dark:border-slate-800/40 text-xs">
                                                <a :href="res.value" target="_blank" class="flex items-center gap-2 text-indigo-600 dark:text-indigo-400 hover:underline font-medium truncate pr-4">
                                                    <Icon :icon="res.type === 'pdf' ? 'tabler:file-text' : 'tabler:link'" class="w-4 h-4 text-slate-400" />
                                                    <span class="truncate">{{ res.name }}</span>
                                                </a>
                                                <span class="text-[9px] font-mono uppercase bg-slate-100 dark:bg-slate-800 text-slate-500 px-1.5 py-0.5 rounded shrink-0">
                                                    {{ res.type }}
                                                </span>
                                            </li>
                                        </ul>
                                        <p v-else class="text-[11px] text-slate-400 italic">No hay archivos o enlaces adjuntos.</p>
                                    </div>

                                    <!-- Evaluations (Tasks) -->
                                    <div class="space-y-3">
                                        <h4 class="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
                                            <Icon icon="tabler:clipboard-check" class="w-4 h-4 text-slate-400" />
                                            Evaluaciones & Tareas
                                        </h4>
                                        <ul v-if="topic.tasks && topic.tasks.length" class="space-y-2">
                                            <li v-for="task in topic.tasks" :key="task.id"
                                                class="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-950/20 rounded-xl border border-slate-100 dark:border-slate-800/40 text-xs">
                                                <div class="space-y-0.5 pr-4 truncate">
                                                    <div class="flex items-center gap-1.5">
                                                        <span :class="[
                                                            task.type === 'examen' ? 'bg-rose-50 text-rose-600 dark:bg-rose-950/40 dark:text-rose-400' :
                                                            task.type === 'practica' ? 'bg-amber-50 text-amber-600 dark:bg-amber-950/40 dark:text-amber-400' :
                                                            'bg-blue-50 text-blue-600 dark:bg-blue-950/40 dark:text-blue-400'
                                                        ]" class="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded">
                                                            {{ task.type }}
                                                        </span>
                                                        <span class="font-semibold text-slate-700 dark:text-slate-200 truncate">{{ task.title }}</span>
                                                    </div>
                                                    <div class="text-[10px] text-slate-400 font-medium">
                                                        Vence: {{ task.due_date ? formatDateTime(task.due_date) : 'Sin fecha límite' }} | Max: {{ task.max_points }} pts
                                                    </div>
                                                </div>

                                                <div v-if="canWrite" class="flex items-center gap-1 shrink-0">
                                                    <button @click="openEditTaskModal(task, topic)"
                                                            class="p-1 text-slate-400 hover:text-indigo-600 rounded transition-all">
                                                        <Icon icon="tabler:edit" class="w-3.5 h-3.5" />
                                                    </button>
                                                    <button @click="deleteTask(task.id)"
                                                            class="p-1 text-slate-400 hover:text-rose-600 rounded transition-all">
                                                        <Icon icon="tabler:trash" class="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                            </li>
                                        </ul>
                                        <p v-else class="text-[11px] text-slate-400 italic">No hay tareas o exámenes asignados para este tema.</p>
                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- TAB 2: ALUMNOS MATRICULADOS -->
                <div v-if="activeTab === 'alumnos'" class="space-y-6">
                    <div class="flex items-center justify-between">
                        <h2 class="text-lg font-bold text-slate-800 dark:text-white">Alumnos Matriculados</h2>
                        <span class="text-xs font-semibold px-3 py-1 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 rounded-full">
                            Total: {{ students.length }} estudiantes
                        </span>
                    </div>

                    <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl shadow-sm overflow-hidden">
                        <div class="overflow-x-auto">
                            <table class="w-full text-left text-xs border-collapse">
                                <thead>
                                    <tr class="bg-slate-50 dark:bg-slate-950/20 text-slate-400 dark:text-slate-500 font-bold border-b border-slate-100 dark:border-slate-800">
                                        <th class="p-4 w-20">Ref</th>
                                        <th class="p-4">Apellidos y Nombres</th>
                                        <th class="p-4 w-32">Documento</th>
                                        <th class="p-4 w-32 text-center">Estado</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="student in students" :key="student.id"
                                        class="border-b border-slate-50 dark:border-slate-800 hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-all font-medium text-slate-700 dark:text-slate-200">
                                        <td class="p-4 font-mono font-bold text-slate-400">{{ student.ref }}</td>
                                        <td class="p-4">
                                            <div class="flex items-center gap-2">
                                                <div class="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-600 dark:text-slate-400 font-bold uppercase">
                                                    {{ student.nombres.substring(0, 1) }}{{ student.apellidos.substring(0, 1) }}
                                                </div>
                                                <span>{{ student.apellidos }}, {{ student.nombres }}</span>
                                            </div>
                                        </td>
                                        <td class="p-4 font-mono">{{ student.num_doc || 'SIN DNI' }}</td>
                                        <td class="p-4 text-center">
                                            <span :class="[student.status ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/50 dark:text-emerald-400' : 'bg-rose-50 text-rose-600 dark:bg-rose-950/50 dark:text-rose-400']"
                                                  class="px-2.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider">
                                                {{ student.status ? 'Activo' : 'Inactivo' }}
                                            </span>
                                        </td>
                                    </tr>
                                    <tr v-if="!students.length">
                                        <td colspan="4" class="p-8 text-center text-slate-400 dark:text-slate-500 italic">
                                            No hay alumnos matriculados en este curso todavía.
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <!-- TAB 3: ASISTENCIA -->
                <div v-if="activeTab === 'asistencia'" class="space-y-6">
                    <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl shadow-sm p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                        <div class="space-y-1 w-full sm:w-auto">
                            <h2 class="text-base font-bold text-slate-800 dark:text-white">Marcar Asistencia Diaria</h2>
                            <p class="text-xs text-slate-400">Selecciona el día para verificar o registrar asistencias.</p>
                        </div>
                        <div class="flex items-center gap-3 w-full sm:w-auto">
                            <input type="date" v-model="attendanceDate"
                                   class="px-3 py-2 text-xs border border-slate-200 dark:border-slate-700/80 rounded-xl bg-white dark:bg-slate-850 text-slate-700 dark:text-white focus:ring-indigo-500 focus:border-indigo-500 transition-all font-semibold" />
                            <button v-if="canWrite" @click="saveAttendance"
                                    class="inline-flex items-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-semibold shadow-sm transition-all shrink-0">
                                <Icon icon="tabler:device-floppy" class="w-4 h-4" />
                                Guardar Registro
                            </button>
                        </div>
                    </div>

                    <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl shadow-sm overflow-hidden">
                        <div class="overflow-x-auto">
                            <table class="w-full text-left text-xs border-collapse">
                                <thead>
                                    <tr class="bg-slate-50 dark:bg-slate-950/20 text-slate-400 dark:text-slate-500 font-bold border-b border-slate-100 dark:border-slate-800">
                                        <th class="p-4">Alumno</th>
                                        <th class="p-4 w-96">Estado de Asistencia</th>
                                        <th class="p-4">Observación</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="record in attendanceRecords" :key="record.id_student"
                                        class="border-b border-slate-50 dark:border-slate-800 hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-all font-medium text-slate-700 dark:text-slate-200">
                                        <td class="p-4 font-semibold">{{ record.apellidos }}, {{ record.nombres }}</td>
                                        <td class="p-4">
                                            <div class="flex flex-wrap items-center gap-1.5">
                                                <button @click="record.status = 'puntual'"
                                                        :class="[record.status === 'puntual' ? 'bg-emerald-500 text-white' : 'bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-400']"
                                                        class="px-2.5 py-1 rounded-lg text-[10px] font-bold transition-all uppercase">
                                                    Puntual
                                                </button>
                                                <button @click="record.status = 'tardanza'"
                                                        :class="[record.status === 'tardanza' ? 'bg-amber-500 text-white' : 'bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-400']"
                                                        class="px-2.5 py-1 rounded-lg text-[10px] font-bold transition-all uppercase">
                                                    Tardanza
                                                </button>
                                                <button @click="record.status = 'tardanza_justificada'"
                                                        :class="[record.status === 'tardanza_justificada' ? 'bg-amber-600 text-white' : 'bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-400']"
                                                        class="px-2.5 py-1 rounded-lg text-[10px] font-bold transition-all uppercase"
                                                        title="Tardanza Justificada">
                                                    Tar. Just.
                                                </button>
                                                <button @click="record.status = 'falta'"
                                                        :class="[record.status === 'falta' ? 'bg-rose-500 text-white' : 'bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-400']"
                                                        class="px-2.5 py-1 rounded-lg text-[10px] font-bold transition-all uppercase">
                                                    Falta
                                                </button>
                                                <button @click="record.status = 'falta_justificada'"
                                                        :class="[record.status === 'falta_justificada' ? 'bg-rose-600 text-white' : 'bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-400']"
                                                        class="px-2.5 py-1 rounded-lg text-[10px] font-bold transition-all uppercase"
                                                        title="Falta Justificada">
                                                    Falt. Just.
                                                </button>
                                            </div>
                                        </td>
                                        <td class="p-4">
                                            <input type="text" v-model="record.observation" placeholder="Comentario / Justificación..."
                                                   class="w-full px-3 py-1.5 text-xs border border-slate-200 dark:border-slate-700/80 rounded-xl bg-white dark:bg-slate-850 text-slate-700 dark:text-white focus:ring-indigo-500 focus:border-indigo-500 transition-all" />
                                        </td>
                                    </tr>
                                    <tr v-if="!attendanceRecords.length">
                                        <td colspan="3" class="p-8 text-center text-slate-400 dark:text-slate-500 italic">
                                            No hay alumnos matriculados para tomar asistencia.
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <!-- TAB 4: FOROS -->
                <div v-if="activeTab === 'foros'" class="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <!-- Forums Sidebar List -->
                    <div class="lg:col-span-1 space-y-4">
                        <div class="flex items-center justify-between">
                            <h2 class="text-base font-bold text-slate-800 dark:text-white">Foros de Discusión</h2>
                            <button v-if="canWrite" @click="openCreateForumModal"
                                    class="p-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-all"
                                    title="Nuevo foro">
                                <Icon icon="tabler:plus" class="w-4 h-4" />
                            </button>
                        </div>

                        <div class="space-y-3 max-h-[600px] overflow-y-auto">
                            <div v-for="forum in forums" :key="forum.id"
                                 @click="openForumThread(forum.id)"
                                 :class="[activeForumId === forum.id ? 'border-indigo-500 bg-indigo-50/20 dark:bg-indigo-950/10' : 'border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900']"
                                 class="p-4 border rounded-2xl cursor-pointer hover:shadow-sm transition-all space-y-2">
                                <div class="flex items-start justify-between gap-3">
                                    <h3 class="text-xs font-bold text-slate-700 dark:text-slate-200 line-clamp-1 pr-2">
                                        {{ forum.title }}
                                    </h3>
                                    <div v-if="canWrite" class="flex items-center gap-1 shrink-0">
                                        <button @click.stop="openEditForumModal(forum)"
                                                class="text-slate-400 hover:text-indigo-600 p-0.5 rounded transition-all">
                                            <Icon icon="tabler:edit" class="w-3.5 h-3.5" />
                                        </button>
                                        <button @click.stop="deleteForum(forum.id)"
                                                class="text-slate-400 hover:text-rose-600 p-0.5 rounded transition-all">
                                            <Icon icon="tabler:trash" class="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </div>
                                <p class="text-[11px] text-slate-400 dark:text-slate-500 line-clamp-2 leading-relaxed">
                                    {{ forum.description }}
                                </p>
                                <div class="flex items-center justify-between text-[9px] text-slate-400 font-bold font-mono">
                                    <span :class="[isForumActive(forum) ? 'text-emerald-500' : 'text-rose-500']" class="uppercase">
                                        {{ isForumActive(forum) ? 'Vigente / Abierto' : 'Cerrado' }}
                                    </span>
                                    <span>
                                        {{ getForumMessageCount(forum.id) }} comentarios
                                    </span>
                                </div>
                            </div>

                            <p v-if="!forums.length" class="text-center text-xs text-slate-400 italic py-8">
                                Aún no se han creado foros para este curso.
                            </p>
                        </div>
                    </div>

                    <!-- Forum Conversation Thread Detail -->
                    <div class="lg:col-span-2 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl p-6 shadow-sm flex flex-col min-h-[500px]">
                        <div v-if="activeForumId" class="flex-1 flex flex-col justify-between space-y-6">
                            <!-- Thread Header -->
                            <div class="border-b border-slate-50 dark:border-slate-800/80 pb-4">
                                <h3 class="text-base font-bold text-slate-800 dark:text-white">
                                    {{ forums.find(f => f.id === activeForumId)?.title }}
                                </h3>
                                <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                    {{ forums.find(f => f.id === activeForumId)?.description }}
                                </p>
                                <div class="flex items-center gap-3 text-[10px] text-slate-400 font-semibold mt-3">
                                    <span class="flex items-center gap-1">
                                        <Icon icon="tabler:calendar-time" class="w-3.5 h-3.5" />
                                        Inicia: {{ formatDateTime(forums.find(f => f.id === activeForumId)?.start_date) }}
                                    </span>
                                    <span class="flex items-center gap-1">
                                        <Icon icon="tabler:calendar-due" class="w-3.5 h-3.5" />
                                        Vence: {{ formatDateTime(forums.find(f => f.id === activeForumId)?.end_date) }}
                                    </span>
                                </div>
                            </div>

                            <!-- Comments Stream -->
                            <div class="flex-1 overflow-y-auto max-h-[400px] space-y-4 pr-2 custom-scrollbar">
                                <div v-for="message in getForumThreadMessages(activeForumId)" :key="message.id"
                                     class="flex gap-3 bg-slate-50/50 dark:bg-slate-950/20 p-3 rounded-2xl border border-slate-100 dark:border-slate-800/40">
                                    <div class="w-8 h-8 rounded-full bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 font-bold flex items-center justify-center text-[10px] uppercase shrink-0">
                                        {{ message.user?.person?.nombres.substring(0, 1) }}{{ message.user?.person?.apellidos.substring(0, 1) }}
                                    </div>
                                    <div class="space-y-1 flex-1">
                                        <div class="flex items-center justify-between text-[10px] text-slate-400">
                                            <span class="font-bold text-slate-700 dark:text-slate-300">
                                                {{ message.user?.person?.apellidos }}, {{ message.user?.person?.nombres }}
                                            </span>
                                            <span class="font-medium">
                                                {{ formatDateTime(message.created_at) }}
                                            </span>
                                        </div>
                                        <p class="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                                            {{ message.message }}
                                        </p>
                                    </div>
                                </div>

                                <p v-if="!getForumThreadMessages(activeForumId).length" class="text-center text-xs text-slate-400 italic py-12">
                                    Nadie ha comentado en este foro. ¡Sé el primero en iniciar la conversación!
                                </p>
                            </div>

                            <!-- Comment Input Form -->
                            <div class="pt-4 border-t border-slate-50 dark:border-slate-800/80">
                                <div v-if="isForumActive(forums.find(f => f.id === activeForumId))" class="flex gap-2">
                                    <textarea v-model="forumMessageForm.message" rows="2" placeholder="Escribe un comentario..."
                                              class="w-full text-xs px-3 py-2 border border-slate-200 dark:border-slate-700/80 rounded-xl bg-white dark:bg-slate-850 text-slate-700 dark:text-white focus:ring-indigo-500 focus:border-indigo-500 transition-all resize-none"></textarea>
                                    <button @click="sendForumMessage"
                                            class="inline-flex items-center justify-center p-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl transition-all self-end shrink-0">
                                        <Icon icon="tabler:send" class="w-5 h-5" />
                                    </button>
                                </div>
                                <div v-else class="p-3 bg-rose-50 dark:bg-rose-950/20 text-rose-600 dark:text-rose-400 rounded-2xl text-xs font-semibold text-center border border-rose-100 dark:border-rose-950/40">
                                    El foro ha sido cerrado y no se admiten comentarios adicionales por fecha de vigencia.
                                </div>
                            </div>
                        </div>

                        <!-- No Forum Selected state -->
                        <div v-else class="flex-1 flex flex-col items-center justify-center text-slate-400 dark:text-slate-500 italic py-16">
                            <Icon icon="tabler:message-square" class="w-12 h-12 text-slate-300 dark:text-slate-600 mb-4" />
                            Selecciona un foro de la lista para ver los mensajes e interactuar.
                        </div>
                    </div>
                </div>

                <!-- TAB 5: CALIFICACIONES (GRADEBOOK VIEW) -->
                <div v-if="activeTab === 'calificaciones'" class="space-y-6">
                    <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl shadow-sm p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                        <div class="space-y-1">
                            <div class="flex items-center gap-2">
                                <h2 class="text-base font-bold text-slate-800 dark:text-white">Registro de Calificaciones (Gradebook)</h2>
                                <button type="button"
                                        @click="refreshGrades"
                                        :disabled="isRefreshingGrades"
                                        class="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-slate-800 rounded-xl transition-all"
                                        :title="isRefreshingGrades ? 'Actualizando...' : 'Actualizar entregas y notas'">
                                    <Icon icon="tabler:refresh" :class="{'animate-spin text-indigo-600 dark:text-indigo-400': isRefreshingGrades}" class="w-4 h-4" />
                                </button>
                            </div>
                            <p class="text-xs text-slate-400">Ingresa notas y feedback para cada evaluación del curso.</p>
                        </div>
                        <div class="flex flex-wrap items-center gap-2 w-full sm:w-auto justify-end">
                            <button type="button"
                                    @click="refreshGrades"
                                    :disabled="isRefreshingGrades"
                                    class="inline-flex items-center gap-1.5 px-3.5 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-xl text-xs font-semibold shadow-xs transition-all shrink-0 hover:scale-[1.02] active:scale-[0.98]"
                                    title="Actualizar y cargar las últimas respuestas y entregas de los alumnos">
                                <Icon icon="tabler:refresh" :class="{'animate-spin text-indigo-600 dark:text-indigo-400': isRefreshingGrades}" class="w-4 h-4 text-slate-500 dark:text-slate-400" />
                                <span>{{ isRefreshingGrades ? 'Actualizando...' : 'Actualizar' }}</span>
                            </button>
                            <button v-if="gradebookTasks.length"
                                    @click="exportGradesToExcel"
                                    :disabled="hasUnsavedChanges"
                                    :class="[
                                        hasUnsavedChanges 
                                            ? 'bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500 opacity-50 cursor-not-allowed' 
                                             : 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm hover:scale-[1.02] active:scale-[0.98]'
                                    ]"
                                    class="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold transition-all duration-250 shrink-0"
                                    :title="hasUnsavedChanges ? 'Guarde las calificaciones modificadas antes de exportar' : 'Exportar calificaciones a Excel'">
                                <Icon icon="tabler:file-spreadsheet" class="w-4 h-4" />
                                Exportar Excel
                            </button>
                            <button v-if="canWrite && gradebookTasks.length" @click="submitGradesForm"
                                    class="inline-flex items-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-semibold shadow-sm transition-all shrink-0 hover:scale-[1.02] active:scale-[0.98]">
                                <Icon icon="tabler:device-floppy" class="w-4 h-4" />
                                Guardar Calificaciones
                            </button>
                        </div>
                    </div>

                    <div v-if="gradebookTasks.length === 0" class="text-center py-16 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl shadow-sm">
                        <Icon icon="tabler:award-off" class="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto" />
                        <h4 class="text-sm font-semibold text-slate-700 dark:text-slate-300 mt-4">No hay evaluaciones registradas</h4>
                        <p class="text-xs text-slate-400 mt-1 max-w-sm mx-auto">Para ingresar calificaciones, primero debes crear tareas, exámenes o prácticas en la pestaña **Contenido**.</p>
                    </div>

                    <div v-else class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl shadow-sm overflow-hidden">
                        <div class="overflow-x-auto">
                            <table class="w-full text-left text-xs border-collapse">
                                <thead>
                                    <tr class="bg-slate-50 dark:bg-slate-950/20 text-slate-400 dark:text-slate-500 font-bold border-b border-slate-100 dark:border-slate-800">
                                        <th class="p-4 min-w-[200px] sticky left-0 bg-slate-50 dark:bg-slate-900 z-10">Alumno</th>
                                        <th v-for="task in gradebookTasks" :key="task.id" class="p-4 w-40 text-center min-w-[120px]">
                                            <div class="font-bold text-slate-700 dark:text-slate-200 line-clamp-1" :title="task.title">
                                                {{ task.title }}
                                            </div>
                                            <div class="text-[9px] font-mono text-slate-400 font-bold uppercase mt-0.5">
                                                {{ task.type }} (max: {{ task.max_points }})
                                            </div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="student in students" :key="student.id"
                                        class="border-b border-slate-50 dark:border-slate-800 hover:bg-slate-50/50 dark:hover:bg-slate-850/30 transition-all font-medium text-slate-700 dark:text-slate-200">
                                        <!-- Sticky Name Column -->
                                        <td class="p-4 font-semibold sticky left-0 bg-white dark:bg-slate-900 z-10 border-r border-slate-100 dark:border-slate-800">
                                            {{ student.apellidos }}, {{ student.nombres }}
                                        </td>
                                        <!-- Inputs for each evaluation task -->
                                        <td v-for="task in gradebookTasks" :key="task.id" class="p-3 text-center transition-all">
                                            <div v-if="getGradeRecord(student.id, task.id)" class="flex flex-col items-center gap-1.5 max-w-[120px] mx-auto">
                                                <!-- Grade input in a nice horizontal row with buttons -->
                                                <div class="flex items-center gap-1">
                                                    <input type="number" 
                                                           v-model="getGradeRecord(student.id, task.id).grade" 
                                                           min="0" :max="task.max_points" step="0.5"
                                                           :class="[
                                                               isGradeInvalid(getGradeRecord(student.id, task.id).grade, task.max_points)
                                                                   ? 'border-rose-500 bg-rose-50 dark:bg-rose-950/30 text-rose-600 dark:text-rose-450 focus:ring-rose-500 focus:border-rose-500'
                                                                   : 'border-slate-200 dark:border-slate-750 bg-white dark:bg-slate-850 text-slate-750 dark:text-white focus:ring-indigo-500 focus:border-indigo-500'
                                                           ]"
                                                           class="w-14 px-1 py-1 text-center font-bold rounded-lg focus:ring-1 text-xs transition-all" />
                                                    
                                                    <!-- Quick Eval Button that opens modal -->
                                                    <button type="button"
                                                            @click="openEvaluationModal(student.id, task.id)"
                                                            :class="[
                                                                getGradeRecord(student.id, task.id).submission_text || getGradeRecord(student.id, task.id).submission_file
                                                                    ? (getGradeRecord(student.id, task.id).status === 'pending'
                                                                        ? 'bg-amber-500/10 hover:bg-amber-500/20 text-amber-600 dark:text-amber-400 border border-amber-500/30 font-bold'
                                                                        : 'bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 font-bold')
                                                                    : 'bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400 border border-transparent'
                                                            ]"
                                                            class="p-1 rounded-lg text-xs transition-all flex items-center justify-center shrink-0 border relative"
                                                            title="Ver Entrega / Detalles">
                                                        <!-- Status Ping Indicator for Pending Submissions -->
                                                        <span v-if="getGradeRecord(student.id, task.id).status === 'pending'" class="absolute -top-0.5 -right-0.5 flex h-2 w-2">
                                                            <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                                                            <span class="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
                                                        </span>
                                                        <Icon :icon="getGradeRecord(student.id, task.id).submission_text || getGradeRecord(student.id, task.id).submission_file ? 'tabler:clipboard-check' : 'tabler:eye'" class="w-4 h-4" />
                                                    </button>
                                                </div>

                                                <!-- Submission Status text/indicator under the grade input -->
                                                <div v-if="getGradeRecord(student.id, task.id).submission_text || getGradeRecord(student.id, task.id).submission_file" class="text-[8px] font-bold tracking-tight">
                                                    <span v-if="getGradeRecord(student.id, task.id).status === 'pending'" class="text-amber-500 uppercase flex items-center gap-0.5">
                                                        <Icon icon="tabler:alert-circle" class="w-2.5 h-2.5" /> Pendiente
                                                    </span>
                                                    <span v-else class="text-emerald-500 uppercase flex items-center gap-0.5">
                                                        <Icon icon="tabler:circle-check" class="w-2.5 h-2.5" /> Calificado
                                                    </span>
                                                </div>
                                                <div v-else class="text-[8px] text-slate-400 italic font-medium">
                                                    Sin entrega
                                                </div>

                                                <!-- Feedback Input -->
                                                <input type="text"
                                                       v-model="getGradeRecord(student.id, task.id).feedback"
                                                       placeholder="Feedback..."
                                                       class="w-24 px-1.5 py-0.5 text-[9px] border border-slate-150 dark:border-slate-700/80 rounded bg-white dark:bg-slate-850 text-slate-500 dark:text-slate-450 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-all truncate"
                                                       :title="getGradeRecord(student.id, task.id).feedback" />
                                            </div>
                                            <span v-else class="text-[10px] text-slate-400 italic">No disponible</span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <!-- TAB 6: FINANCIERA -->
                <div v-if="activeTab === 'financiera'" class="space-y-6">
                    
                    <!-- Header Banner Financiero -->
                    <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl shadow-sm p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                        <div class="space-y-1">
                            <div class="flex items-center gap-2">
                                <span class="text-[9px] font-bold font-mono px-2 py-0.5 bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400 rounded uppercase">
                                    Módulo Financiero
                                </span>
                                <span v-if="!canFinanzasReadAll" class="text-[9px] font-bold font-mono px-2 py-0.5 bg-amber-50 dark:bg-amber-950/50 text-amber-600 dark:text-amber-400 rounded uppercase">
                                    Modo Lectura (Precios Ocultos)
                                </span>
                            </div>
                            <h2 class="text-base font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                <Icon icon="tabler:wallet" class="w-5 h-5 text-emerald-500" />
                                Estado Financiero & Cobranzas del Curso
                            </h2>
                            <p class="text-xs text-slate-400 max-w-2xl leading-relaxed">
                                Supervisa los ingresos generados, el control de cuotas por periodo, los alumnos con saldo pendiente y el historial de órdenes de pago emitidas para este curso.
                            </p>
                        </div>
                        <div class="flex flex-wrap items-center gap-2 shrink-0">
                            <button type="button" @click="exportFinanceToExcel"
                                    class="inline-flex items-center gap-1.5 px-3.5 py-2 bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-950/40 dark:hover:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 rounded-xl text-xs font-semibold transition-all">
                                <Icon icon="tabler:file-spreadsheet" class="w-4 h-4 text-emerald-600" />
                                Exportar Excel
                            </button>
                            <Link v-if="hasRight('finanzas', 'read_formacion')" :href="route('financiera-formacion.index')"
                                  class="inline-flex items-center gap-1.5 px-3.5 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl text-xs font-semibold transition-all">
                                <Icon icon="tabler:external-link" class="w-4 h-4 text-indigo-500" />
                                Ir a Finanzas Global
                            </Link>
                        </div>
                    </div>

                    <!-- Precio del Curso & KPIs -->
                    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                        <!-- Tarjeta Precio Establecido del Curso -->
                        <div class="bg-white dark:bg-slate-900 border border-indigo-100 dark:border-indigo-950/70 rounded-3xl p-5 shadow-sm space-y-2 relative overflow-hidden bg-gradient-to-br from-indigo-50/40 to-white dark:from-slate-900 dark:to-slate-900">
                            <div class="flex items-center justify-between text-slate-400 text-xs font-semibold">
                                <span class="uppercase tracking-wider text-[10px] font-bold text-indigo-900 dark:text-indigo-300">Precio Establecido</span>
                                <Icon icon="tabler:tag" class="w-4 h-4 text-indigo-500" />
                            </div>
                            <div v-if="!isEditingPrice">
                                <div class="text-xl font-black text-slate-800 dark:text-white">
                                    <span v-if="canFinanzasReadAll">S/ {{ (parseFloat(coursePriceInput) || 0).toFixed(2) }}</span>
                                    <span v-else class="text-slate-400">***</span>
                                </div>
                                <div class="flex items-center justify-between mt-2 pt-1 border-t border-slate-100 dark:border-slate-800">
                                    <span class="text-[11px] text-slate-400">Por periodo / cuota</span>
                                    <button v-if="canFinanzasWrite" @click="isEditingPrice = true" class="inline-flex items-center gap-1 text-[11px] font-bold text-indigo-600 dark:text-indigo-400 hover:underline">
                                        <Icon icon="tabler:edit" class="w-3.5 h-3.5" />
                                        Modificar
                                    </button>
                                </div>
                            </div>
                            <div v-else class="space-y-2 pt-1">
                                <div class="relative">
                                    <span class="absolute left-2.5 top-2 text-xs font-bold text-slate-400">S/</span>
                                    <input type="number" step="0.01" min="0" v-model="coursePriceInput" class="w-full text-xs pl-7 pr-2.5 py-1.5 border border-indigo-300 dark:border-indigo-600 rounded-xl dark:bg-slate-850 dark:text-white font-bold focus:ring-indigo-500 focus:border-indigo-500" placeholder="0.00" />
                                </div>
                                <div class="flex items-center gap-1.5">
                                    <button @click="saveCoursePrice" :disabled="isSavingPrice" class="flex-1 py-1.5 bg-indigo-600 text-white text-[10px] font-bold rounded-lg hover:bg-indigo-700 transition-all flex items-center justify-center gap-1 shadow-xs">
                                        <Icon icon="tabler:check" class="w-3.5 h-3.5" />
                                        {{ isSavingPrice ? '...' : 'Guardar' }}
                                    </button>
                                    <button @click="isEditingPrice = false; coursePriceInput = parseFloat(course.price || 0)" class="px-2 py-1.5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-[10px] rounded-lg hover:bg-slate-200">
                                        Cancelar
                                    </button>
                                </div>
                            </div>
                        </div>

                        <!-- KPI 1: Órdenes -->
                        <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl p-5 shadow-sm space-y-2">
                            <div class="flex items-center justify-between text-slate-400 text-xs font-semibold">
                                <span class="uppercase tracking-wider text-[10px]">Total Órdenes</span>
                                <Icon icon="tabler:file-invoice" class="w-4 h-4 text-slate-400" />
                            </div>
                            <div class="text-xl font-black text-slate-800 dark:text-white">
                                <span v-if="canFinanzasReadAll">S/ {{ (financeData.stats?.total_billed || 0).toFixed(2) }}</span>
                                <span v-else class="text-slate-400">***</span>
                            </div>
                            <div class="text-[11px] text-slate-400">{{ financeData.stats?.total_orders || 0 }} órdenes emitidas</div>
                        </div>

                        <!-- KPI 2: Recaudado -->
                        <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl p-5 shadow-sm space-y-2">
                            <div class="flex items-center justify-between text-slate-400 text-xs font-semibold">
                                <span class="uppercase tracking-wider text-[10px]">Total Recaudado</span>
                                <Icon icon="tabler:cash" class="w-4 h-4 text-emerald-500" />
                            </div>
                            <div class="text-xl font-black text-emerald-600 dark:text-emerald-400">
                                <span v-if="canFinanzasReadAll">S/ {{ (financeData.stats?.total_paid || 0).toFixed(2) }}</span>
                                <span v-else class="text-slate-400">***</span>
                            </div>
                            <div class="text-[11px] text-emerald-600 font-semibold">{{ financeData.stats?.paid_orders_count || 0 }} órdenes pagadas</div>
                        </div>

                        <!-- KPI 3: Saldo Deudor -->
                        <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl p-5 shadow-sm space-y-2">
                            <div class="flex items-center justify-between text-slate-400 text-xs font-semibold">
                                <span class="uppercase tracking-wider text-[10px]">Saldo Pendiente</span>
                                <Icon icon="tabler:alert-triangle" class="w-4 h-4 text-rose-500" />
                            </div>
                            <div class="text-xl font-black text-rose-600 dark:text-rose-400">
                                <span v-if="canFinanzasReadAll">S/ {{ (financeData.stats?.total_balance || 0).toFixed(2) }}</span>
                                <span v-else class="text-slate-400">***</span>
                            </div>
                            <div class="text-[11px] text-rose-500 font-semibold">{{ financeData.stats?.pending_orders_count || 0 }} órdenes por cobrar</div>
                        </div>

                        <!-- KPI 4: Tasa & Matriculados -->
                        <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl p-5 shadow-sm space-y-2">
                            <div class="flex items-center justify-between text-slate-400 text-xs font-semibold">
                                <span class="uppercase tracking-wider text-[10px]">Cobranza & Estado</span>
                                <Icon icon="tabler:percentage" class="w-4 h-4 text-indigo-500" />
                            </div>
                            <div class="text-xl font-black text-indigo-600 dark:text-indigo-400">
                                {{ financeData.stats?.collection_rate || 0 }}%
                            </div>
                            <div class="text-[11px] text-slate-400">
                                <span class="text-emerald-600 font-bold">{{ financeData.stats?.up_to_date_count || 0 }}</span> al día / 
                                <span class="text-rose-500 font-bold">{{ financeData.stats?.debtors_count || 0 }}</span> con deuda
                            </div>
                        </div>
                    </div>

                    <!-- Resumen por Periodo Configurado -->
                    <div v-if="financeData.periods_summary && financeData.periods_summary.length > 0" class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl p-6 shadow-sm space-y-4">
                        <div class="flex items-center justify-between">
                            <h3 class="text-sm font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                <Icon icon="tabler:calendar-stats" class="w-4 h-4 text-indigo-500" />
                                Desglose Financiero por Periodo del Curso
                            </h3>
                            <span class="text-xs text-slate-400">{{ financeData.periods_summary.length }} periodos configurados</span>
                        </div>

                        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <div v-for="ps in financeData.periods_summary" :key="ps.id" class="p-4 rounded-2xl bg-slate-50 dark:bg-slate-850/60 border border-slate-100 dark:border-slate-800 space-y-2">
                                <div class="flex items-center justify-between">
                                    <span class="text-xs font-bold text-slate-800 dark:text-slate-200">{{ ps.label }}</span>
                                    <span class="text-[10px] font-mono px-2 py-0.5 bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 rounded">{{ ps.periodo }}</span>
                                </div>
                                <div class="text-[11px] text-slate-400">
                                    Vigencia: {{ ps.start_date || '-' }} al {{ ps.end_date || '-' }}
                                </div>
                                <div class="pt-1 flex items-center justify-between text-xs font-semibold">
                                    <span class="text-slate-500">Recaudado:</span>
                                    <span v-if="canFinanzasReadAll" class="text-emerald-600">S/ {{ (ps.total_paid || 0).toFixed(2) }} / S/ {{ (ps.total_billed || 0).toFixed(2) }}</span>
                                    <span v-else class="text-slate-400">***</span>
                                </div>
                                <div class="w-full bg-slate-200 dark:bg-slate-700 h-1.5 rounded-full overflow-hidden">
                                    <div class="bg-indigo-600 h-full rounded-full transition-all" :style="{ width: `${ps.collection_rate}%` }"></div>
                                </div>
                                <div class="flex items-center justify-between text-[10px] text-slate-400 pt-0.5">
                                    <span>{{ ps.total_orders }} órdenes</span>
                                    <span class="font-bold text-indigo-600 dark:text-indigo-400">{{ ps.collection_rate }}% cobrado</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Alumnos Deudores y Órdenes -->
                    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        
                        <!-- Panel de Alumnos Deudores -->
                        <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl p-6 shadow-sm space-y-4">
                            <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                <div class="flex items-center gap-2">
                                    <Icon icon="tabler:user-exclamation" class="w-5 h-5 text-rose-500" />
                                    <h3 class="text-sm font-bold text-slate-800 dark:text-white">
                                        Alumnos con Pagos Pendientes ({{ filteredDebtors.length }})
                                    </h3>
                                </div>
                                <div class="relative">
                                    <input type="text" v-model="financeDebtorSearch" placeholder="Buscar alumno..."
                                           class="text-xs px-3 py-1.5 pl-8 border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-850 text-slate-700 dark:text-slate-200 w-full sm:w-44" />
                                    <Icon icon="tabler:search" class="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-2.5" />
                                </div>
                            </div>

                            <div v-if="filteredDebtors.length === 0" class="text-center py-10 text-xs text-slate-400 border border-dashed rounded-2xl">
                                <Icon icon="tabler:circle-check" class="w-8 h-8 text-emerald-500 mx-auto mb-1.5" />
                                No hay alumnos con pagos pendientes en este curso.
                            </div>

                            <div v-else class="space-y-2.5 max-h-[420px] overflow-y-auto pr-1">
                                <div v-for="debtor in filteredDebtors" :key="debtor.id"
                                     class="p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-850/60 border border-slate-100 dark:border-slate-800 space-y-2">
                                    <div class="flex items-center justify-between">
                                        <div>
                                            <span class="text-xs font-bold text-slate-800 dark:text-slate-200">{{ debtor.apellidos }} {{ debtor.nombres }}</span>
                                            <div class="text-[11px] text-slate-400 flex items-center gap-2 mt-0.5">
                                                <span v-if="debtor.num_doc">DNI: {{ debtor.num_doc }}</span>
                                                <span v-if="debtor.celular">Cel: {{ debtor.celular }}</span>
                                            </div>
                                        </div>
                                        <div class="text-right">
                                            <div class="text-xs font-extrabold text-rose-600 dark:text-rose-400">
                                                <span v-if="canFinanzasReadAll">S/ {{ (debtor.total_debt || 0).toFixed(2) }}</span>
                                                <span v-else class="text-slate-400">***</span>
                                            </div>
                                            <span class="text-[10px] text-rose-500 font-semibold">{{ debtor.pending_orders_count }} cuota(s)</span>
                                        </div>
                                    </div>
                                    <div class="pt-1 flex flex-wrap gap-1.5">
                                        <span v-for="(p, pIdx) in debtor.pending_periods" :key="pIdx"
                                              class="text-[9px] px-2 py-0.5 bg-rose-50 dark:bg-rose-950 text-rose-700 dark:text-rose-300 rounded font-mono">
                                            {{ p.periodo }} (S/ {{ canFinanzasReadAll ? p.balance.toFixed(2) : '***' }})
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Panel de Órdenes de Pago -->
                        <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl p-6 shadow-sm space-y-4">
                            <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                <div class="flex items-center gap-2">
                                    <Icon icon="tabler:receipt-2" class="w-5 h-5 text-indigo-500" />
                                    <h3 class="text-sm font-bold text-slate-800 dark:text-white">
                                        Órdenes de Pago del Curso ({{ filteredCourseOrders.length }})
                                    </h3>
                                </div>
                                <div class="flex items-center gap-2">
                                    <select v-model="financeOrderStatus" class="text-xs py-1.5 px-2.5 border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-850 text-slate-700 dark:text-slate-200">
                                        <option value="all">Todos</option>
                                        <option value="0">Pendientes</option>
                                        <option value="2">Parciales</option>
                                        <option value="1">Pagados</option>
                                    </select>
                                    <div class="relative">
                                        <input type="text" v-model="financeOrderSearch" placeholder="Buscar orden..."
                                               class="text-xs px-3 py-1.5 pl-8 border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-850 text-slate-700 dark:text-slate-200 w-full sm:w-36" />
                                        <Icon icon="tabler:search" class="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-2.5" />
                                    </div>
                                </div>
                            </div>

                            <div v-if="filteredCourseOrders.length === 0" class="text-center py-10 text-xs text-slate-400 border border-dashed rounded-2xl">
                                No se encontraron órdenes para los filtros aplicados.
                            </div>

                            <div v-else class="space-y-2.5 max-h-[420px] overflow-y-auto pr-1">
                                <div v-for="ord in filteredCourseOrders" :key="ord.id"
                                     class="p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-850/60 border border-slate-100 dark:border-slate-800 flex items-center justify-between gap-3">
                                    <div class="space-y-1">
                                        <div class="flex items-center gap-2">
                                            <span class="text-xs font-bold text-slate-800 dark:text-slate-200">{{ ord.student?.person?.apellidos }} {{ ord.student?.person?.nombres }}</span>
                                            <span class="text-[9px] font-mono px-1.5 py-0.5 bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded">{{ ord.ref }}</span>
                                        </div>
                                        <div class="text-[11px] text-slate-400">
                                            {{ ord.label }} (Periodo: {{ ord.periodo }})
                                        </div>
                                    </div>
                                    <div class="text-right shrink-0">
                                        <div class="text-xs font-bold text-slate-800 dark:text-slate-200">
                                            <span v-if="canFinanzasReadAll">S/ {{ parseFloat(ord.total_amount || 0).toFixed(2) }}</span>
                                            <span v-else class="text-slate-400">***</span>
                                        </div>
                                        <span v-if="ord.status === 1" class="text-[9px] font-bold px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full">
                                            Pagado
                                        </span>
                                        <span v-else-if="ord.status === 2" class="text-[9px] font-bold px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full">
                                            Parcial (S/ {{ canFinanzasReadAll ? parseFloat(ord.balance).toFixed(2) : '***' }})
                                        </span>
                                        <span v-else class="text-[9px] font-bold px-2 py-0.5 bg-rose-100 text-rose-700 rounded-full">
                                            Pendiente
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>

                </div>

                <!-- TAB 7: CONFIGURACIÓN -->
                <div v-if="activeTab === 'configuracion'" class="space-y-6">
                    
                    <!-- Header Banner de Configuración con Sub-tabs -->
                    <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl shadow-sm p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                        <div class="space-y-1">
                            <div class="flex items-center gap-2">
                                <span class="text-[9px] font-bold font-mono px-2 py-0.5 bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400 rounded uppercase">
                                    Ajustes del Curso
                                </span>
                                <span v-if="!canSetupWrite" class="text-[9px] font-bold font-mono px-2 py-0.5 bg-amber-50 dark:bg-amber-950/50 text-amber-600 dark:text-amber-400 rounded uppercase">
                                    Modo Lectura
                                </span>
                            </div>
                            <h2 class="text-base font-bold text-slate-800 dark:text-white">Configuración del Curso de Formación</h2>
                            <p class="text-xs text-slate-400 max-w-2xl leading-relaxed">
                                Gestiona las fechas generales, los periodos/módulos académicos y el sistema dinámico de criterios de evaluación.
                            </p>
                        </div>

                        <!-- Sub-tab Selector Pills -->
                        <div class="flex items-center p-1.5 bg-slate-100 dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shrink-0">
                            <button type="button" @click="subTabConfig = 'general'"
                                    :class="[subTabConfig === 'general' ? 'bg-white dark:bg-slate-900 text-indigo-600 dark:text-indigo-400 shadow-xs font-bold' : 'text-slate-500 hover:text-slate-800 dark:text-slate-400']"
                                    class="flex items-center gap-2 px-4 py-2 rounded-xl text-xs transition-all">
                                <Icon icon="tabler:calendar-cog" class="w-4 h-4" />
                                1. General (Fechas & Periodos)
                            </button>
                            <button type="button" @click="subTabConfig = 'criterios'"
                                    :class="[subTabConfig === 'criterios' ? 'bg-white dark:bg-slate-900 text-indigo-600 dark:text-indigo-400 shadow-xs font-bold' : 'text-slate-500 hover:text-slate-800 dark:text-slate-400']"
                                    class="flex items-center gap-2 px-4 py-2 rounded-xl text-xs transition-all">
                                <Icon icon="tabler:adjustments-horizontal" class="w-4 h-4" />
                                2. Criterios de Evaluación
                            </button>
                        </div>
                    </div>

                    <!-- SUB-TAB 1: GENERAL (Fechas y Periodos) -->
                    <div v-if="subTabConfig === 'general'" class="space-y-6">
                        
                        <!-- Fechas Generales del Curso -->
                        <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl p-6 shadow-sm space-y-4">
                            <div class="flex items-center gap-2 text-slate-800 dark:text-white font-bold text-sm">
                                <Icon icon="tabler:calendar-time" class="w-4 h-4 text-indigo-500" />
                                Fechas Generales del Curso
                            </div>
                            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
                                <div>
                                    <label class="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">Fecha de Inicio del Curso</label>
                                    <input type="date" v-model="generalSetupForm.start_date" :disabled="!canSetupWrite"
                                           class="w-full text-xs px-3 py-2 border border-slate-200 dark:border-slate-700/80 rounded-xl bg-white dark:bg-slate-850 text-slate-700 dark:text-white focus:ring-indigo-500 focus:border-indigo-500 font-semibold" />
                                </div>
                                <div>
                                    <label class="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">Fecha de Fin del Curso</label>
                                    <input type="date" v-model="generalSetupForm.end_date" :disabled="!canSetupWrite"
                                           class="w-full text-xs px-3 py-2 border border-slate-200 dark:border-slate-700/80 rounded-xl bg-white dark:bg-slate-850 text-slate-700 dark:text-white focus:ring-indigo-500 focus:border-indigo-500 font-semibold" />
                                </div>
                            </div>
                        </div>

                        <!-- Periodos del Curso -->
                        <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl p-6 shadow-sm space-y-4">
                            <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                <div class="space-y-0.5">
                                    <div class="flex items-center gap-2 text-slate-800 dark:text-white font-bold text-sm">
                                        <Icon icon="tabler:calendar-month" class="w-4 h-4 text-indigo-500" />
                                        Periodos Académicos del Curso
                                    </div>
                                    <p class="text-xs text-slate-400">
                                        Configura los periodos (ej. Mes 1, Mes 2, etc.) con sus códigos y fechas de vigencia para la programación masiva de órdenes.
                                    </p>
                                </div>
                                <div v-if="canSetupWrite" class="flex flex-wrap items-center gap-2 shrink-0">
                                    <button type="button" @click="generateSixDefaultPeriods"
                                            class="inline-flex items-center gap-1.5 px-3 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl text-xs font-semibold transition-all">
                                        <Icon icon="tabler:wand" class="w-4 h-4 text-indigo-500" />
                                        Generar 6 Meses
                                    </button>
                                    <button type="button" @click="addPeriod"
                                            class="inline-flex items-center gap-1.5 px-3.5 py-2 bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-950/50 dark:hover:bg-indigo-900/50 text-indigo-600 dark:text-indigo-300 rounded-xl text-xs font-bold transition-all">
                                        <Icon icon="tabler:plus" class="w-4 h-4" />
                                        Agregar Periodo
                                    </button>
                                </div>
                            </div>

                            <!-- Empty State Periodos -->
                            <div v-if="!generalSetupForm.periods || generalSetupForm.periods.length === 0"
                                 class="text-center py-12 border border-dashed border-slate-200 dark:border-slate-800 rounded-2xl space-y-3">
                                <Icon icon="tabler:calendar-off" class="w-10 h-10 text-slate-300 dark:text-slate-600 mx-auto" />
                                <p class="text-xs text-slate-400">Aún no se han configurado periodos para este curso. Haz clic en "Agregar Periodo" o "Generar 6 Meses".</p>
                            </div>

                            <!-- Lista de Periodos -->
                            <div v-else class="space-y-3 pt-2">
                                <div v-for="(period, pIdx) in generalSetupForm.periods" :key="period.id || pIdx"
                                     class="flex flex-col sm:flex-row sm:items-center gap-3 p-4 bg-slate-50 dark:bg-slate-950/40 border border-slate-100 dark:border-slate-800/80 rounded-2xl transition-all hover:shadow-xs">
                                    
                                    <!-- Index Badge -->
                                    <div class="flex items-center gap-2 sm:w-8 shrink-0">
                                        <span class="w-6 h-6 rounded-full bg-indigo-600 text-white font-bold text-[10px] flex items-center justify-center">
                                            {{ pIdx + 1 }}
                                        </span>
                                    </div>

                                    <!-- Period Code (ej. 202601) -->
                                    <div class="w-full sm:w-36 space-y-1">
                                        <label class="block text-[10px] font-bold uppercase tracking-wider text-slate-400">Periodo (Código)</label>
                                        <input type="text" v-model="period.periodo" required placeholder="Ej: 202601" :disabled="!canSetupWrite"
                                               class="w-full text-xs px-3 py-2 border border-slate-200 dark:border-slate-700/80 rounded-xl bg-white dark:bg-slate-850 text-slate-700 dark:text-white font-mono font-bold focus:ring-indigo-500 focus:border-indigo-500" />
                                    </div>

                                    <!-- Period Label (ej. Inglés - Mes 1) -->
                                    <div class="flex-1 space-y-1">
                                        <label class="block text-[10px] font-bold uppercase tracking-wider text-slate-400">Nombre / Glosa del Periodo</label>
                                        <input type="text" v-model="period.label" required placeholder="Ej: Inglés - Mes 1" :disabled="!canSetupWrite"
                                               class="w-full text-xs px-3 py-2 border border-slate-200 dark:border-slate-700/80 rounded-xl bg-white dark:bg-slate-850 text-slate-700 dark:text-white font-semibold focus:ring-indigo-500 focus:border-indigo-500" />
                                    </div>

                                    <!-- Start Date -->
                                    <div class="w-full sm:w-40 space-y-1">
                                        <label class="block text-[10px] font-bold uppercase tracking-wider text-slate-400">Fecha de Inicio</label>
                                        <input type="date" v-model="period.start_date" :disabled="!canSetupWrite"
                                               class="w-full text-xs px-3 py-2 border border-slate-200 dark:border-slate-700/80 rounded-xl bg-white dark:bg-slate-850 text-slate-700 dark:text-white focus:ring-indigo-500 focus:border-indigo-500" />
                                    </div>

                                    <!-- End Date -->
                                    <div class="w-full sm:w-40 space-y-1">
                                        <label class="block text-[10px] font-bold uppercase tracking-wider text-slate-400">Fecha de Fin</label>
                                        <input type="date" v-model="period.end_date" :disabled="!canSetupWrite"
                                               class="w-full text-xs px-3 py-2 border border-slate-200 dark:border-slate-700/80 rounded-xl bg-white dark:bg-slate-850 text-slate-700 dark:text-white focus:ring-indigo-500 focus:border-indigo-500" />
                                    </div>

                                    <!-- Delete button -->
                                    <div v-if="canSetupWrite" class="flex sm:flex-col items-center justify-end sm:pt-4">
                                        <button type="button" @click="removePeriod(pIdx)"
                                                class="p-2 text-rose-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded-xl transition-all"
                                                title="Eliminar este periodo">
                                            <Icon icon="tabler:trash" class="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Botón Guardar Configuración General -->
                        <div v-if="canSetupWrite" class="flex justify-end pt-2">
                            <button type="button" @click="saveGeneralSetup" :disabled="isSavingGeneralSetup"
                                    class="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl text-xs font-bold shadow-md shadow-indigo-100 dark:shadow-none transition-all hover:scale-[1.01] active:scale-[0.99]">
                                <Icon icon="tabler:device-floppy" class="w-4 h-4" />
                                <span>{{ isSavingGeneralSetup ? 'Guardando...' : 'Guardar Configuración General' }}</span>
                            </button>
                        </div>

                    </div>

                    <!-- SUB-TAB 2: CRITERIOS DE EVALUACIÓN -->
                    <div v-if="subTabConfig === 'criterios'" class="space-y-6">
                        
                        <!-- Header Banner Criterios -->
                        <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl shadow-sm p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                            <div class="space-y-1">
                                <h2 class="text-base font-bold text-slate-800 dark:text-white">Criterios de Evaluación Dinámicos</h2>
                                <p class="text-xs text-slate-400 max-w-2xl leading-relaxed">
                                    Define los criterios específicos de evaluación para este curso (ej. Listening, Reading, Writing, Speaking). Al crear exámenes o evaluaciones, se desglosará el puntaje según estos criterios.
                                </p>
                            </div>
                            <div v-if="canSetupWrite" class="flex flex-wrap items-center gap-2 shrink-0">
                                <button type="button" @click="loadPresetLanguages"
                                        class="inline-flex items-center gap-1.5 px-3 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl text-xs font-semibold transition-all">
                                    <Icon icon="tabler:language" class="w-4 h-4 text-indigo-500" />
                                    Plantilla Idiomas (4 Skills)
                                </button>
                                <button type="button" @click="loadPresetStandard"
                                        class="inline-flex items-center gap-1.5 px-3 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl text-xs font-semibold transition-all">
                                    <Icon icon="tabler:school" class="w-4 h-4 text-emerald-500" />
                                    Plantilla General
                                </button>
                            </div>
                        </div>

                        <!-- Main Form / Settings Card -->
                        <form @submit.prevent="submitCriteriaConfig" class="space-y-6">
                            
                            <!-- General Settings Grid -->
                            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                
                                <!-- 1. Calculation Method -->
                                <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl p-6 shadow-sm space-y-4">
                                    <div class="flex items-center gap-2 text-slate-800 dark:text-white font-bold text-sm">
                                        <Icon icon="tabler:calculator" class="w-4 h-4 text-indigo-500" />
                                        Cálculo de la Nota Total
                                    </div>
                                    <p class="text-xs text-slate-400">
                                        Determina cómo se procesará el puntaje final de la evaluación a partir de los criterios registrados:
                                    </p>
                                    
                                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                                        <label :class="[
                                            criteriaConfigForm.evaluation_criteria.calculation_type === 'promedio'
                                                ? 'border-indigo-600 dark:border-indigo-500 bg-indigo-50/40 dark:bg-indigo-950/20 text-indigo-900 dark:text-indigo-200'
                                                : 'border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-850/50 text-slate-700 dark:text-slate-300'
                                        ]" class="flex flex-col p-4 rounded-2xl border-2 cursor-pointer transition-all">
                                            <div class="flex items-center justify-between">
                                                <span class="text-xs font-bold flex items-center gap-1.5">
                                                    <input type="radio" value="promedio" v-model="criteriaConfigForm.evaluation_criteria.calculation_type" class="text-indigo-600 focus:ring-indigo-500" />
                                                    Promedio Ponderado
                                                </span>
                                                <span class="text-[9px] font-mono uppercase bg-indigo-100 dark:bg-indigo-900/60 text-indigo-600 dark:text-indigo-300 px-1.5 py-0.5 rounded font-bold">Por defecto</span>
                                            </div>
                                            <span class="text-[11px] text-slate-400 dark:text-slate-400 mt-2 leading-tight">
                                                Se calcula el promedio ponderado de los criterios evaluados usando el peso asignado.
                                            </span>
                                        </label>

                                        <label :class="[
                                            criteriaConfigForm.evaluation_criteria.calculation_type === 'suma'
                                                ? 'border-indigo-600 dark:border-indigo-500 bg-indigo-50/40 dark:bg-indigo-950/20 text-indigo-900 dark:text-indigo-200'
                                                : 'border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-850/50 text-slate-700 dark:text-slate-300'
                                        ]" class="flex flex-col p-4 rounded-2xl border-2 cursor-pointer transition-all">
                                            <div class="flex items-center justify-between">
                                                <span class="text-xs font-bold flex items-center gap-1.5">
                                                    <input type="radio" value="suma" v-model="criteriaConfigForm.evaluation_criteria.calculation_type" class="text-indigo-600 focus:ring-indigo-500" />
                                                    Suma de Puntos
                                                </span>
                                            </div>
                                            <span class="text-[11px] text-slate-400 dark:text-slate-400 mt-2 leading-tight">
                                                El puntaje final es la suma directa de los puntos obtenidos en cada criterio.
                                            </span>
                                        </label>
                                    </div>
                                </div>

                                <!-- 2. Applicable Evaluation Types -->
                                <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl p-6 shadow-sm space-y-4">
                                    <div class="flex items-center gap-2 text-slate-800 dark:text-white font-bold text-sm">
                                        <Icon icon="tabler:checks" class="w-4 h-4 text-emerald-500" />
                                        Aplicar Criterios en Evaluaciones de Tipo
                                    </div>
                                    <p class="text-xs text-slate-400">
                                        Selecciona en qué tipos de evaluaciones se activará automáticamente el desglose de criterios:
                                    </p>

                                    <div class="flex flex-wrap gap-2.5 pt-1">
                                        <button type="button" @click="toggleAppliedType('examen')"
                                                :class="[
                                                    criteriaConfigForm.evaluation_criteria.applied_types?.includes('examen')
                                                        ? 'bg-rose-50 border-rose-300 dark:bg-rose-950/40 dark:border-rose-700 text-rose-700 dark:text-rose-300 font-bold shadow-sm'
                                                        : 'bg-slate-50 border-slate-200 dark:bg-slate-850 dark:border-slate-700 text-slate-500 hover:text-slate-700'
                                                ]"
                                                class="inline-flex items-center gap-2 px-3.5 py-2.5 rounded-xl border text-xs transition-all">
                                            <Icon icon="tabler:file-certificate" class="w-4 h-4 text-rose-500" />
                                            <span>Examen / Prueba</span>
                                            <Icon :icon="criteriaConfigForm.evaluation_criteria.applied_types?.includes('examen') ? 'tabler:circle-check-filled' : 'tabler:circle'" class="w-4 h-4" />
                                        </button>

                                        <button type="button" @click="toggleAppliedType('tarea')"
                                                :class="[
                                                    criteriaConfigForm.evaluation_criteria.applied_types?.includes('tarea')
                                                        ? 'bg-indigo-50 border-indigo-300 dark:bg-indigo-950/40 dark:border-indigo-700 text-indigo-700 dark:text-indigo-300 font-bold shadow-sm'
                                                        : 'bg-slate-50 border-slate-200 dark:bg-slate-850 dark:border-slate-700 text-slate-500 hover:text-slate-700'
                                                ]"
                                                class="inline-flex items-center gap-2 px-3.5 py-2.5 rounded-xl border text-xs transition-all">
                                            <Icon icon="tabler:notes" class="w-4 h-4 text-indigo-500" />
                                            <span>Tarea / Deber</span>
                                            <Icon :icon="criteriaConfigForm.evaluation_criteria.applied_types?.includes('tarea') ? 'tabler:circle-check-filled' : 'tabler:circle'" class="w-4 h-4" />
                                        </button>

                                        <button type="button" @click="toggleAppliedType('practica')"
                                                :class="[
                                                    criteriaConfigForm.evaluation_criteria.applied_types?.includes('practica')
                                                        ? 'bg-amber-50 border-amber-300 dark:bg-amber-950/40 dark:border-amber-700 text-amber-700 dark:text-amber-300 font-bold shadow-sm'
                                                        : 'bg-slate-50 border-slate-200 dark:bg-slate-850 dark:border-slate-700 text-slate-500 hover:text-slate-700'
                                                ]"
                                                class="inline-flex items-center gap-2 px-3.5 py-2.5 rounded-xl border text-xs transition-all">
                                            <Icon icon="tabler:pencil" class="w-4 h-4 text-amber-500" />
                                            <span>Práctica / Taller</span>
                                            <Icon :icon="criteriaConfigForm.evaluation_criteria.applied_types?.includes('practica') ? 'tabler:circle-check-filled' : 'tabler:circle'" class="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>

                            </div>

                            <!-- 3. List of Criteria Items -->
                            <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl p-6 shadow-sm space-y-4">
                                <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                    <div class="space-y-0.5">
                                        <div class="flex items-center gap-2 text-slate-800 dark:text-white font-bold text-sm">
                                            <Icon icon="tabler:list-details" class="w-4 h-4 text-indigo-500" />
                                            Criterios de Evaluación Configurados
                                        </div>
                                        <p class="text-xs text-slate-400">
                                            Agrega los criterios que componen las evaluaciones. Puedes definir su peso y puntaje máximo sugerido.
                                        </p>
                                    </div>
                                    <button v-if="canSetupWrite" type="button" @click="addCriterion"
                                            class="inline-flex items-center gap-1.5 px-3.5 py-2 bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-950/50 dark:hover:bg-indigo-900/50 text-indigo-600 dark:text-indigo-300 rounded-xl text-xs font-bold transition-all shrink-0">
                                        <Icon icon="tabler:plus" class="w-4 h-4" />
                                        Agregar Criterio
                                    </button>
                                </div>

                                <!-- Empty state -->
                                <div v-if="!criteriaConfigForm.evaluation_criteria.criteria || criteriaConfigForm.evaluation_criteria.criteria.length === 0"
                                     class="text-center py-12 border border-dashed border-slate-200 dark:border-slate-800 rounded-2xl space-y-3">
                                    <Icon icon="tabler:clipboard-x" class="w-10 h-10 text-slate-300 dark:text-slate-600 mx-auto" />
                                    <p class="text-xs text-slate-400">No hay criterios agregados. Haz clic en "Agregar Criterio" o carga una plantilla rápida.</p>
                                </div>

                                <!-- Criteria table / list -->
                                <div v-else class="space-y-3 pt-2">
                                    <div v-for="(crit, idx) in criteriaConfigForm.evaluation_criteria.criteria" :key="crit.id"
                                         class="flex flex-col sm:flex-row sm:items-center gap-3 p-3.5 bg-slate-50 dark:bg-slate-950/40 border border-slate-100 dark:border-slate-800/80 rounded-2xl transition-all hover:shadow-xs">
                                        
                                        <!-- Index Badge -->
                                        <div class="flex items-center gap-2 sm:w-8 shrink-0">
                                            <span class="w-6 h-6 rounded-full bg-indigo-600 text-white font-bold text-[10px] flex items-center justify-center">
                                                {{ idx + 1 }}
                                            </span>
                                        </div>

                                        <!-- Criterion Name -->
                                        <div class="flex-1 space-y-1">
                                            <label class="block text-[10px] font-bold uppercase tracking-wider text-slate-400">Nombre del Criterio</label>
                                            <input type="text" v-model="crit.name" required placeholder="Ej: Listening, Reading, etc." :disabled="!canSetupWrite"
                                                   class="w-full text-xs px-3 py-2 border border-slate-200 dark:border-slate-700/80 rounded-xl bg-white dark:bg-slate-850 text-slate-700 dark:text-white focus:ring-indigo-500 focus:border-indigo-500 font-semibold" />
                                        </div>

                                        <!-- Weight (Peso) -->
                                        <div class="w-full sm:w-28 space-y-1">
                                            <label class="block text-[10px] font-bold uppercase tracking-wider text-slate-400">Peso (Pond.)</label>
                                            <input type="number" v-model="crit.weight" required min="0.1" step="0.1" :disabled="!canSetupWrite"
                                                   class="w-full text-xs px-3 py-2 border border-slate-200 dark:border-slate-700/80 rounded-xl bg-white dark:bg-slate-850 text-slate-700 dark:text-white text-center font-bold focus:ring-indigo-500 focus:border-indigo-500" />
                                        </div>

                                        <!-- Default Max Score -->
                                        <div class="w-full sm:w-36 space-y-1">
                                            <label class="block text-[10px] font-bold uppercase tracking-wider text-slate-400">Puntaje Max. Sugerido</label>
                                            <input type="number" v-model="crit.max_score" required min="1" step="1" :disabled="!canSetupWrite"
                                                   class="w-full text-xs px-3 py-2 border border-slate-200 dark:border-slate-700/80 rounded-xl bg-white dark:bg-slate-850 text-slate-700 dark:text-white text-center font-bold focus:ring-indigo-500 focus:border-indigo-500" />
                                        </div>

                                        <!-- Actions -->
                                        <div v-if="canSetupWrite" class="flex sm:flex-col items-center justify-end sm:pt-4">
                                            <button type="button" @click="removeCriterion(idx)"
                                                    class="p-2 text-rose-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded-xl transition-all"
                                                    title="Eliminar este criterio">
                                                <Icon icon="tabler:trash" class="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Submit Footer -->
                            <div v-if="canSetupWrite" class="flex justify-end pt-2">
                                <button type="submit" :disabled="criteriaConfigForm.processing"
                                        class="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl text-xs font-bold shadow-md shadow-indigo-100 dark:shadow-none transition-all hover:scale-[1.01] active:scale-[0.99]">
                                    <Icon icon="tabler:device-floppy" class="w-4 h-4" />
                                    <span>{{ criteriaConfigForm.processing ? 'Guardando...' : 'Guardar Configuración de Criterios' }}</span>
                                </button>
                            </div>

                        </form>
                    </div>

                </div>

            </div>
        </div>

        <!-- MODAL: CREAR / EDITAR TEMA -->
        <div v-if="showTopicModal" class="fixed inset-0 z-50 flex items-center justify-center overflow-x-hidden overflow-y-auto outline-none focus:outline-none">
            <div class="fixed inset-0 bg-slate-900/60 dark:bg-slate-950/80 backdrop-blur-sm transition-opacity" @click="showTopicModal = false"></div>
            <div class="relative w-full max-w-2xl mx-auto my-6 z-10 px-4">
                <div class="border border-slate-100 dark:border-slate-800 rounded-3xl shadow-xl relative flex flex-col w-full bg-white dark:bg-slate-900 outline-none focus:outline-none overflow-hidden transition-all duration-300">
                    <!-- Header -->
                    <div class="flex items-center justify-between p-6 border-b border-slate-50 dark:border-slate-800/80">
                        <h3 class="text-base font-bold text-slate-800 dark:text-white">
                            {{ isEditingTopic ? 'Editar Tema de la Semana' : 'Crear Tema de la Semana' }}
                        </h3>
                        <button @click="showTopicModal = false" class="text-slate-400 hover:text-slate-600 rounded-lg p-1 transition-all">
                            <Icon icon="tabler:x" class="w-5 h-5" />
                        </button>
                    </div>

                    <form @submit.prevent="submitTopicForm" class="p-6 space-y-4">
                        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label class="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">Semana N°</label>
                                <input type="number" v-model="topicForm.week_number" required min="1"
                                       class="w-full text-xs px-3 py-2 border border-slate-200 dark:border-slate-700/80 rounded-xl bg-white dark:bg-slate-850 text-slate-700 dark:text-white focus:ring-indigo-500 focus:border-indigo-500 transition-all" />
                            </div>
                            <div>
                                <label class="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">Orden Visual</label>
                                <input type="number" v-model="topicForm.sort_order" required min="1"
                                       class="w-full text-xs px-3 py-2 border border-slate-200 dark:border-slate-700/80 rounded-xl bg-white dark:bg-slate-850 text-slate-700 dark:text-white focus:ring-indigo-500 focus:border-indigo-500 transition-all" />
                            </div>
                            <div>
                                <label class="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">Fecha Inicio</label>
                                <input type="date" v-model="topicForm.start_date"
                                       class="w-full text-xs px-3 py-2 border border-slate-200 dark:border-slate-700/80 rounded-xl bg-white dark:bg-slate-850 text-slate-700 dark:text-white focus:ring-indigo-500 focus:border-indigo-500 transition-all" />
                            </div>
                        </div>

                        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div>
                                                <label class="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">Fecha Fin</label>
                                                <input type="date" v-model="topicForm.end_date"
                                                       class="w-full text-xs px-3 py-2 border border-slate-200 dark:border-slate-700/80 rounded-xl bg-white dark:bg-slate-850 text-slate-700 dark:text-white focus:ring-indigo-500 focus:border-indigo-500 transition-all" />
                                            </div>
                                            <div>
                                                <label class="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">Título del Tema</label>
                                                <input type="text" v-model="topicForm.title" required placeholder="Ej: Introducción a Robótica"
                                                       class="w-full text-xs px-3 py-2 border border-slate-200 dark:border-slate-700/80 rounded-xl bg-white dark:bg-slate-850 text-slate-700 dark:text-white focus:ring-indigo-500 focus:border-indigo-500 transition-all" />
                                            </div>
                                            <div>
                                                <label class="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">Estado</label>
                                                <select v-model="topicForm.status"
                                                        class="w-full text-xs px-3 py-2 border border-slate-200 dark:border-slate-700/80 rounded-xl bg-white dark:bg-slate-850 text-slate-700 dark:text-white focus:ring-indigo-500 focus:border-indigo-500 transition-all">
                                                    <option :value="0">Borrador</option>
                                                    <option :value="1">Validado</option>
                                                </select>
                                            </div>
                                        </div>

                        <div>
                            <label class="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">Descripción</label>
                            <textarea v-model="topicForm.description" rows="3" placeholder="Resumen del tema y objetivos..."
                                      class="w-full text-xs px-3 py-2 border border-slate-200 dark:border-slate-700/80 rounded-xl bg-white dark:bg-slate-850 text-slate-700 dark:text-white focus:ring-indigo-500 focus:border-indigo-500 transition-all"></textarea>
                        </div>

                        <!-- Resource additions -->
                        <div class="border-t border-slate-50 dark:border-slate-800/80 pt-4 space-y-3">
                            <label class="block text-xs font-bold text-slate-600 dark:text-slate-300">Recursos de Estudio (PDFs o Links)</label>
                            
                            <div class="flex flex-col sm:flex-row gap-2">
                                <input type="text" v-model="newResourceName" placeholder="Nombre de etiqueta (ej: Lectura Complementaria)"
                                       class="flex-1 text-xs px-3 py-2 border border-slate-200 dark:border-slate-700/80 rounded-xl bg-white dark:bg-slate-850 text-slate-700 dark:text-white" />
                                
                                <select v-model="newResourceType"
                                        class="w-full sm:w-28 text-xs px-3 py-2 border border-slate-200 dark:border-slate-700/80 rounded-xl bg-white dark:bg-slate-850 text-slate-700 dark:text-white">
                                    <option value="link">Link URL</option>
                                    <option value="pdf">PDF File</option>
                                </select>
                            </div>

                            <div class="flex gap-2">
                                <input v-if="newResourceType === 'link'" type="url" v-model="newResourceValue" placeholder="https://ejemplo.com/recurso"
                                       class="flex-1 text-xs px-3 py-2 border border-slate-200 dark:border-slate-700/80 rounded-xl bg-white dark:bg-slate-850 text-slate-700 dark:text-white" />
                                
                                <div v-else class="flex-1 flex items-center gap-3">
                                    <input type="file" ref="fileInput" @change="handleFileUpload" accept="application/pdf"
                                           class="hidden" />
                                    <button type="button" @click="$refs.fileInput.click()" :disabled="isUploading"
                                            class="inline-flex items-center gap-2 px-3 py-2 border border-slate-200 dark:border-slate-700/80 text-xs rounded-xl bg-slate-50 hover:bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-semibold transition-all">
                                        <Icon icon="tabler:upload" class="w-4 h-4" />
                                        {{ isUploading ? 'Subiendo...' : 'Seleccionar Archivo PDF' }}
                                    </button>
                                </div>

                                <button v-if="newResourceType === 'link'" type="button" @click="addResource"
                                        class="px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-xl text-xs font-semibold transition-all shrink-0">
                                    Agregar
                                </button>
                            </div>

                            <!-- List of temporarily added resources -->
                            <div v-if="topicForm.resources && topicForm.resources.length" class="space-y-2 mt-3 bg-slate-50/50 dark:bg-slate-950/20 p-3 rounded-2xl border border-slate-100 dark:border-slate-800/40">
                                <div v-for="(res, idx) in topicForm.resources" :key="idx"
                                     class="flex items-center justify-between text-xs p-1.5 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800/80 rounded-xl">
                                    <span class="font-medium text-slate-700 dark:text-slate-300 truncate pr-4 flex items-center gap-1.5">
                                        <Icon :icon="res.type === 'pdf' ? 'tabler:file-text' : 'tabler:link'" class="w-4 h-4 text-slate-400" />
                                        {{ res.name }}
                                    </span>
                                    <button type="button" @click="removeResource(idx)" class="text-rose-500 hover:text-rose-700 transition-all p-1">
                                        <Icon icon="tabler:trash" class="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        </div>

                        <!-- Footer -->
                        <div class="flex justify-end gap-3 pt-6 border-t border-slate-50 dark:border-slate-800/80">
                            <button type="button" @click="showTopicModal = false"
                                    class="px-4 py-2 border border-slate-200 dark:border-slate-750 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-850 rounded-xl text-xs font-semibold transition-all">
                                Cancelar
                            </button>
                            <button type="submit" :disabled="topicForm.processing"
                                    class="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-semibold shadow-sm transition-all">
                                {{ isEditingTopic ? 'Guardar Cambios' : 'Crear Tema' }}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>

        <!-- MODAL: CREAR / EDITAR EVALUACIÓN -->
        <div v-if="showTaskModal" class="fixed inset-0 z-50 flex items-center justify-center overflow-x-hidden overflow-y-auto outline-none focus:outline-none">
            <div class="fixed inset-0 bg-slate-900/60 dark:bg-slate-950/80 backdrop-blur-sm transition-opacity" @click="showTaskModal = false"></div>
            <div class="relative w-full max-w-lg mx-auto my-6 z-10 px-4">
                <div class="border border-slate-100 dark:border-slate-800 rounded-3xl shadow-xl relative flex flex-col w-full bg-white dark:bg-slate-900 outline-none focus:outline-none overflow-hidden transition-all duration-300">
                    <!-- Header -->
                    <div class="flex items-center justify-between p-6 border-b border-slate-50 dark:border-slate-800/80">
                        <div class="space-y-0.5">
                            <h3 class="text-base font-bold text-slate-800 dark:text-white">
                                {{ isEditingTask ? 'Editar Evaluación' : 'Crear Nueva Evaluación' }}
                            </h3>
                            <p class="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">
                                Tema: {{ currentTopicForTask?.title }}
                            </p>
                        </div>
                        <button @click="showTaskModal = false" class="text-slate-400 hover:text-slate-600 rounded-lg p-1 transition-all">
                            <Icon icon="tabler:x" class="w-5 h-5" />
                        </button>
                    </div>

                    <form @submit.prevent="submitTaskForm" class="p-6 space-y-4">
                        <div>
                            <label class="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">Título de la Evaluación</label>
                            <input type="text" v-model="taskForm.title" required placeholder="Ej: Tarea N° 1 - Redactar Ensayo"
                                   class="w-full text-xs px-3 py-2 border border-slate-200 dark:border-slate-700/80 rounded-xl bg-white dark:bg-slate-850 text-slate-700 dark:text-white focus:ring-indigo-500 focus:border-indigo-500 transition-all" />
                        </div>

                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label class="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">Tipo</label>
                                <select v-model="taskForm.type" @change="onTaskTypeChange"
                                        class="w-full text-xs px-3 py-2 border border-slate-200 dark:border-slate-700/80 rounded-xl bg-white dark:bg-slate-850 text-slate-700 dark:text-white focus:ring-indigo-500 focus:border-indigo-500 transition-all font-semibold">
                                    <option value="tarea">Tarea / Deber</option>
                                    <option value="examen">Examen / Prueba</option>
                                    <option value="practica">Práctica / Taller</option>
                                </select>
                            </div>
                            <div>
                                <label class="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">Puntos Máximos</label>
                                <div class="relative">
                                    <input type="number" v-model="taskForm.max_points" required min="1"
                                           :readonly="taskForm.evaluation_criteria && taskForm.evaluation_criteria.length > 0"
                                           :class="[
                                               taskForm.evaluation_criteria && taskForm.evaluation_criteria.length > 0 
                                                   ? 'bg-slate-50 dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 font-bold border-indigo-200 dark:border-indigo-800' 
                                                   : 'bg-white dark:bg-slate-850 text-slate-700 dark:text-white border-slate-200 dark:border-slate-700/80'
                                           ]"
                                           class="w-full text-xs px-3 py-2 border rounded-xl focus:ring-indigo-500 focus:border-indigo-500 transition-all" />
                                    <span v-if="taskForm.evaluation_criteria && taskForm.evaluation_criteria.length > 0" 
                                          class="absolute right-2.5 top-2 text-[10px] font-bold text-indigo-500 uppercase">
                                        Auto ({{ taskForm.calculation_type === 'suma' ? 'Suma' : 'Prom.' }})
                                    </span>
                                </div>
                            </div>
                        </div>

                        <!-- Dynamic Evaluation Criteria breakdown if applicable -->
                        <div v-if="(props.course.evaluation_criteria?.applied_types || ['examen']).includes(taskForm.type) && (props.course.evaluation_criteria?.criteria?.length > 0 || (taskForm.evaluation_criteria && taskForm.evaluation_criteria.length > 0))"
                             class="p-4 bg-indigo-50/40 dark:bg-indigo-950/20 border border-indigo-100 dark:border-indigo-900/40 rounded-2xl space-y-3">
                            <div class="flex items-center justify-between gap-2 flex-wrap">
                                <div class="flex items-center gap-1.5 text-xs font-bold text-indigo-900 dark:text-indigo-200">
                                    <Icon icon="tabler:adjustments" class="w-4 h-4 text-indigo-500" />
                                    Criterios de Evaluación Aplicables
                                </div>
                                <div class="flex items-center gap-2">
                                    <button v-if="availableCourseCriteria.length > 0"
                                            type="button"
                                            @click="resetTaskCriteriaToCourse"
                                            class="text-[10px] font-semibold text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-0.5"
                                            title="Restablecer todos los criterios del curso">
                                        <Icon icon="tabler:rotate-clockwise" class="w-3 h-3" />
                                        Restablecer todos
                                    </button>
                                    <span class="text-[10px] font-bold px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/60 text-indigo-700 dark:text-indigo-300 rounded-md uppercase">
                                        Modo: {{ taskForm.calculation_type === 'suma' ? 'Suma de puntos' : 'Promedio' }}
                                    </span>
                                </div>
                            </div>
                            <p class="text-[11px] text-slate-500 dark:text-slate-400 leading-tight">
                                Puedes ajustar el puntaje máximo o quitar criterios que no apliquen a esta evaluación. El total de "Puntos Máximos" se actualizará automáticamente.
                            </p>

                            <!-- Active Criteria Grid -->
                            <div v-if="taskForm.evaluation_criteria && taskForm.evaluation_criteria.length > 0" class="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-1">
                                <div v-for="(crit, critIdx) in taskForm.evaluation_criteria" :key="crit.id || critIdx"
                                     class="group relative flex items-center justify-between p-2.5 bg-white dark:bg-slate-900 border border-indigo-100/80 dark:border-indigo-900/40 rounded-xl shadow-xs hover:border-indigo-300 dark:hover:border-indigo-700 transition-all">
                                    <div class="space-y-0.5 truncate pr-2">
                                        <span class="text-xs font-bold text-slate-700 dark:text-slate-200 block truncate" :title="crit.name">{{ crit.name }}</span>
                                        <span class="text-[9px] font-mono text-slate-400 font-semibold">Peso: {{ crit.weight }}</span>
                                    </div>
                                    <div class="flex items-center gap-1.5 shrink-0">
                                        <span class="text-[10px] font-bold text-slate-400">Max:</span>
                                        <input type="number" v-model="crit.max_score" @input="onCriterionMaxScoreChange" required min="1"
                                               class="w-14 px-2 py-1 text-xs text-center font-bold border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-850 text-slate-800 dark:text-white focus:ring-1 focus:ring-indigo-500" />
                                        <button type="button" @click="removeTaskCriterion(critIdx)"
                                                class="p-1 text-slate-300 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded-lg transition-all"
                                                title="Quitar este criterio de la evaluación">
                                            <Icon icon="tabler:x" class="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <!-- Empty state if all criteria were removed -->
                            <div v-else class="p-3 bg-white/60 dark:bg-slate-900/60 border border-dashed border-indigo-200 dark:border-indigo-900/60 rounded-xl text-center space-y-1">
                                <p class="text-xs text-slate-500 dark:text-slate-400 font-medium">
                                    Has quitado todos los criterios. Esta evaluación se calificará con un puntaje global directo.
                                </p>
                            </div>

                            <!-- Available criteria to re-add -->
                            <div v-if="availableCourseCriteria.length > 0" class="pt-2 border-t border-indigo-100/60 dark:border-indigo-900/30 flex flex-wrap items-center gap-1.5">
                                <span class="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase">Agregar:</span>
                                <button v-for="avail in availableCourseCriteria" :key="avail.id"
                                        type="button"
                                        @click="addCourseCriterionToTask(avail)"
                                        class="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold bg-white dark:bg-slate-900 hover:bg-indigo-50 dark:hover:bg-indigo-950/60 text-slate-600 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-300 border border-slate-200 dark:border-slate-800 rounded-lg transition-all shadow-2xs">
                                    <Icon icon="tabler:plus" class="w-3 h-3 text-indigo-500" />
                                    <span>{{ avail.name }}</span>
                                </button>
                            </div>
                        </div>

                        <div>
                            <label class="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">Fecha Límite de Entrega</label>
                            <input type="datetime-local" v-model="taskForm.due_date"
                                   class="w-full text-xs px-3 py-2 border border-slate-200 dark:border-slate-700/80 rounded-xl bg-white dark:bg-slate-850 text-slate-700 dark:text-white focus:ring-indigo-500 focus:border-indigo-500 transition-all" />
                        </div>

                        <div>
                            <label class="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">Instrucciones</label>
                            <textarea v-model="taskForm.description" rows="3" placeholder="Detalles de la entrega y criterios..."
                                      class="w-full text-xs px-3 py-2 border border-slate-200 dark:border-slate-700/80 rounded-xl bg-white dark:bg-slate-850 text-slate-700 dark:text-white focus:ring-indigo-500 focus:border-indigo-500 transition-all"></textarea>
                        </div>

                        <!-- Footer -->
                        <div class="flex justify-end gap-3 pt-6 border-t border-slate-50 dark:border-slate-800/80">
                            <button type="button" @click="showTaskModal = false"
                                    class="px-4 py-2 border border-slate-200 dark:border-slate-750 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-850 rounded-xl text-xs font-semibold transition-all">
                                Cancelar
                            </button>
                            <button type="submit" :disabled="taskForm.processing"
                                    class="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-semibold shadow-sm transition-all">
                                {{ isEditingTask ? 'Guardar Cambios' : 'Crear Evaluación' }}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>

        <!-- MODAL: CREAR FORO -->
        <div v-if="showForumModal" class="fixed inset-0 z-50 flex items-center justify-center overflow-x-hidden overflow-y-auto outline-none focus:outline-none">
            <div class="fixed inset-0 bg-slate-900/60 dark:bg-slate-950/80 backdrop-blur-sm transition-opacity" @click="showForumModal = false"></div>
            <div class="relative w-full max-w-lg mx-auto my-6 z-10 px-4">
                <div class="border border-slate-100 dark:border-slate-800 rounded-3xl shadow-xl relative flex flex-col w-full bg-white dark:bg-slate-900 outline-none focus:outline-none overflow-hidden transition-all duration-300">
                    <!-- Header -->
                    <div class="flex items-center justify-between p-6 border-b border-slate-50 dark:border-slate-800/80">
                        <h3 class="text-base font-bold text-slate-800 dark:text-white">
                            {{ isEditingForum ? 'Editar Foro de Discusión' : 'Crear Foro de Discusión' }}
                        </h3>
                        <button @click="showForumModal = false" class="text-slate-400 hover:text-slate-600 rounded-lg p-1 transition-all">
                            <Icon icon="tabler:x" class="w-5 h-5" />
                        </button>
                    </div>

                    <form @submit.prevent="submitForumForm" class="p-6 space-y-4">
                        <div>
                            <label class="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">Título del Foro</label>
                            <input type="text" v-model="forumForm.title" required placeholder="Ej: Foro Temático N°1 - Feedback"
                                   class="w-full text-xs px-3 py-2 border border-slate-200 dark:border-slate-700/80 rounded-xl bg-white dark:bg-slate-850 text-slate-700 dark:text-white focus:ring-indigo-500 focus:border-indigo-500 transition-all" />
                        </div>

                        <div>
                            <label class="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">Descripción / Pregunta Semilla</label>
                            <textarea v-model="forumForm.description" required rows="3" placeholder="Detalles de la consigna y puntos de interacción..."
                                      class="w-full text-xs px-3 py-2 border border-slate-200 dark:border-slate-700/80 rounded-xl bg-white dark:bg-slate-850 text-slate-700 dark:text-white focus:ring-indigo-500 focus:border-indigo-500 transition-all"></textarea>
                        </div>

                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label class="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">Fecha Apertura (Inicia)</label>
                                <input type="datetime-local" v-model="forumForm.start_date" required
                                       class="w-full text-xs px-3 py-2 border border-slate-200 dark:border-slate-700/80 rounded-xl bg-white dark:bg-slate-850 text-slate-700 dark:text-white focus:ring-indigo-500 focus:border-indigo-500 transition-all" />
                            </div>
                            <div>
                                <label class="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">Fecha Cierre (Finaliza)</label>
                                <input type="datetime-local" v-model="forumForm.end_date" required
                                       class="w-full text-xs px-3 py-2 border border-slate-200 dark:border-slate-700/80 rounded-xl bg-white dark:bg-slate-850 text-slate-700 dark:text-white focus:ring-indigo-500 focus:border-indigo-500 transition-all" />
                            </div>
                        </div>

                        <!-- Footer -->
                        <div class="flex justify-end gap-3 pt-6 border-t border-slate-50 dark:border-slate-800/80">
                            <button type="button" @click="showForumModal = false"
                                    class="px-4 py-2 border border-slate-200 dark:border-slate-750 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-850 rounded-xl text-xs font-semibold transition-all">
                                Cancelar
                            </button>
                            <button type="submit" :disabled="forumForm.processing"
                                    class="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-semibold shadow-sm transition-all">
                                {{ isEditingForum ? 'Guardar Cambios' : 'Crear Foro' }}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>

        <!-- MODAL: EVALUACIÓN Y CALIFICACIÓN DE ENTREGA (Grade Evaluation Panel) -->
        <div v-if="showEvaluationModal" class="fixed inset-0 z-50 flex items-center justify-center overflow-x-hidden overflow-y-auto outline-none focus:outline-none">
            <div class="fixed inset-0 bg-slate-900/60 dark:bg-slate-950/80 backdrop-blur-sm transition-opacity" @click="showEvaluationModal = false"></div>
            <div class="relative w-full max-w-2xl mx-auto my-6 z-10 px-4">
                <div class="border border-slate-150 dark:border-slate-800 rounded-3xl shadow-2xl relative flex flex-col w-full bg-white dark:bg-slate-900 outline-none focus:outline-none overflow-hidden transition-all duration-300">
                    
                    <!-- Header -->
                    <div class="flex items-center justify-between p-6 border-b border-slate-50 dark:border-slate-800/80 bg-slate-50/50 dark:bg-slate-950/20">
                        <div class="space-y-1">
                            <span class="text-[9px] font-extrabold uppercase tracking-wider px-2 py-0.5 bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 rounded-md">
                                Panel de Evaluación de Entrega
                            </span>
                            <h3 class="text-base font-extrabold text-slate-850 dark:text-white mt-1.5">
                                {{ activeEvaluationRecord?.student_name }}
                            </h3>
                            <p class="text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                                {{ activeEvaluationRecord?.task_title }}
                            </p>
                        </div>
                        <button @click="showEvaluationModal = false" class="text-slate-450 hover:text-slate-600 dark:hover:text-slate-200 rounded-xl p-1.5 hover:bg-slate-105 dark:hover:bg-slate-800 transition-all">
                            <Icon icon="tabler:x" class="w-5 h-5" />
                        </button>
                    </div>

                    <!-- Body -->
                    <div class="p-6 space-y-6 max-h-[60vh] overflow-y-auto custom-scrollbar">
                        
                        <!-- Submission Date & Status Badge -->
                        <div class="flex flex-wrap items-center justify-between gap-4 p-4 bg-slate-50 dark:bg-slate-950/30 rounded-2xl border border-slate-100/80 dark:border-slate-800/60 text-xs">
                            <div class="flex items-center gap-2">
                                <Icon icon="tabler:calendar-time" class="w-4 h-4 text-slate-400" />
                                <span class="font-medium text-slate-500 dark:text-slate-455">Fecha de entrega:</span>
                                <span class="font-bold text-slate-700 dark:text-slate-355">
                                    {{ activeEvaluationRecord?.submitted_at ? formatDateTime(activeEvaluationRecord.submitted_at) : 'No registrada (calificación directa)' }}
                                </span>
                            </div>
                            <div>
                                <span v-if="activeEvaluationRecord?.submission_text || activeEvaluationRecord?.submission_file"
                                      :class="[
                                          activeEvaluationRecord?.status === 'pending'
                                              ? 'bg-amber-50 text-amber-600 dark:bg-amber-950/40 dark:text-amber-400 border border-amber-250/20'
                                              : 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400 border border-emerald-250/20'
                                      ]"
                                      class="px-2.5 py-1 rounded-xl text-[10px] font-extrabold uppercase tracking-wider border">
                                    {{ activeEvaluationRecord?.status === 'pending' ? 'Pendiente de Revisión' : 'Calificado' }}
                                </span>
                                <span v-else class="px-2.5 py-1 bg-slate-100 text-slate-550 dark:bg-slate-800 dark:text-slate-455 border border-slate-200 dark:border-slate-700 rounded-xl text-[10px] font-extrabold uppercase tracking-wider">
                                    Sin Envío Formal
                                </span>
                            </div>
                        </div>

                        <!-- Written Response (Text Submission) -->
                        <div class="space-y-2">
                            <h4 class="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
                                <Icon icon="tabler:align-left" class="w-4 h-4 text-slate-455" />
                                Respuesta Escrita del Alumno
                            </h4>
                            <div v-if="activeEvaluationRecord?.submission_text" 
                                 class="p-4 bg-slate-50 dark:bg-slate-950/40 rounded-2xl border border-slate-100/80 dark:border-slate-800/80 text-xs text-slate-650 dark:text-slate-355 leading-relaxed whitespace-pre-wrap select-text custom-scrollbar max-h-48 overflow-y-auto">
                                {{ activeEvaluationRecord.submission_text }}
                            </div>
                            <div v-else class="text-xs text-slate-400 dark:text-slate-500 italic p-4 bg-slate-50/50 dark:bg-slate-950/10 rounded-2xl border border-dashed border-slate-200 dark:border-slate-850 text-center">
                                El alumno no ingresó respuesta de texto escrita.
                            </div>
                        </div>

                        <!-- Attachment (File Submission) -->
                        <div class="space-y-2">
                            <h4 class="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
                                <Icon icon="tabler:paperclip" class="w-4 h-4 text-slate-455" />
                                Archivo Adjunto Entregado
                            </h4>
                            <div v-if="activeEvaluationRecord?.submission_file" class="flex items-center justify-between p-3.5 bg-slate-50 dark:bg-slate-950/40 rounded-2xl border border-slate-100/80 dark:border-slate-800/85 text-xs">
                                <div class="flex items-center gap-3 overflow-hidden pr-4">
                                    <div class="p-2.5 bg-rose-50 dark:bg-rose-950/50 text-rose-600 dark:text-rose-455 rounded-xl">
                                        <Icon icon="tabler:file-text" class="w-5 h-5" />
                                    </div>
                                    <div class="space-y-0.5 overflow-hidden">
                                        <span class="font-bold text-slate-700 dark:text-slate-300 block truncate" :title="activeEvaluationRecord.submission_file.split('/').pop()">
                                            {{ activeEvaluationRecord.submission_file.split('/').pop() }}
                                        </span>
                                        <span class="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">Documento de Entrega</span>
                                    </div>
                                </div>
                                <a :href="activeEvaluationRecord.submission_file" target="_blank"
                                   class="inline-flex items-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl shadow-md shadow-indigo-100 dark:shadow-none transition-all duration-200 shrink-0">
                                    <Icon icon="tabler:download" class="w-4 h-4" />
                                    Descargar
                                </a>
                            </div>
                            <div v-else class="text-xs text-slate-400 dark:text-slate-500 italic p-4 bg-slate-50/50 dark:bg-slate-950/10 rounded-2xl border border-dashed border-slate-200 dark:border-slate-850 text-center">
                                No se adjuntó ningún archivo.
                            </div>
                        </div>

                        <!-- Criteria Grading Breakdown (if task has evaluation criteria) -->
                        <div v-if="activeEvaluationRecord?.evaluation_criteria && activeEvaluationRecord.evaluation_criteria.length > 0"
                             class="space-y-3 p-4 bg-indigo-50/40 dark:bg-indigo-950/30 border border-indigo-100 dark:border-indigo-900/40 rounded-2xl">
                            <div class="flex items-center justify-between">
                                <h4 class="text-xs font-bold text-indigo-900 dark:text-indigo-200 uppercase tracking-wider flex items-center gap-1.5">
                                    <Icon icon="tabler:adjustments" class="w-4 h-4 text-indigo-500" />
                                    Calificación por Criterios de Evaluación
                                </h4>
                                <span class="text-[10px] font-bold px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/60 text-indigo-700 dark:text-indigo-300 rounded-md uppercase">
                                    Cálculo: {{ activeEvaluationRecord.calculation_type === 'suma' ? 'Suma de Puntos' : 'Promedio Ponderado' }}
                                </span>
                            </div>
                            <p class="text-[11px] text-slate-500 dark:text-slate-400 leading-tight">
                                Registra los puntos de cada criterio. El puntaje total obtenido se calculará automáticamente.
                            </p>

                            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-1">
                                <div v-for="crit in activeEvaluationRecord.evaluation_criteria" :key="crit.id"
                                     class="p-3 bg-white dark:bg-slate-900 border border-indigo-100/80 dark:border-indigo-900/40 rounded-xl space-y-1.5 shadow-xs">
                                    <div class="flex items-center justify-between">
                                        <span class="text-xs font-bold text-slate-700 dark:text-slate-200 block truncate" :title="crit.name">
                                            {{ crit.name }}
                                        </span>
                                        <span class="text-[9px] font-mono text-slate-400 font-semibold">Peso: {{ crit.weight }}</span>
                                    </div>
                                    
                                    <div class="relative flex items-center">
                                        <input type="number" 
                                               v-model="activeEvaluationRecord.criteria_grades[crit.id]" 
                                               @input="onCriterionGradeChange"
                                               min="0" :max="crit.max_score" step="0.5"
                                               placeholder="0.0"
                                               :class="[
                                                   isGradeInvalid(activeEvaluationRecord.criteria_grades[crit.id], crit.max_score)
                                                       ? 'border-rose-500 bg-rose-50 dark:bg-rose-950/30 text-rose-600 focus:ring-rose-500 focus:border-rose-500'
                                                       : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-850 text-slate-800 dark:text-white focus:ring-indigo-500 focus:border-indigo-500'
                                               ]"
                                               class="w-full text-xs font-bold px-2 py-1.5 rounded-lg border text-center transition-all pr-10" />
                                        <span class="absolute right-2 text-[10px] font-bold text-slate-400">
                                            / {{ crit.max_score }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Grading Section -->
                        <div class="grid grid-cols-1 md:grid-cols-4 gap-6 pt-4 border-t border-slate-100 dark:border-slate-800/60">
                            <!-- Puntaje Obtenido -->
                            <div class="md:col-span-1 space-y-1.5">
                                <label class="block text-[10px] font-extrabold uppercase tracking-wider text-slate-455 mb-1">Puntaje Obtenido</label>
                                <div class="relative flex items-center">
                                    <input type="number" v-model="activeEvaluationRecord.grade" required min="0" :max="activeEvaluationRecord.max_points" step="0.5"
                                           :class="[
                                               isGradeInvalid(activeEvaluationRecord.grade, activeEvaluationRecord.max_points)
                                                   ? 'border-rose-500 bg-rose-50 dark:bg-rose-950/30 text-rose-600 dark:text-rose-455 focus:ring-rose-500 focus:border-rose-500'
                                                   : 'border-slate-250 dark:border-slate-700/80 bg-white dark:bg-slate-855 text-slate-800 dark:text-white focus:ring-indigo-500 focus:border-indigo-500'
                                           ]"
                                           class="w-full text-sm font-extrabold px-3 py-2.5 rounded-xl border focus:ring-2 transition-all pr-12 text-center" />
                                    <span class="absolute right-3 text-xs font-bold text-slate-400">
                                        / {{ activeEvaluationRecord.max_points }}
                                    </span>
                                </div>
                                <span class="text-[9px] text-slate-400 block mt-1 font-semibold">Max: {{ activeEvaluationRecord.max_points }} pts</span>
                            </div>

                            <!-- Estado de Entrega -->
                            <div class="md:col-span-1 space-y-1.5">
                                <label class="block text-[10px] font-extrabold uppercase tracking-wider text-slate-455 mb-1">Estado de Entrega</label>
                                <select v-model="activeEvaluationRecord.status"
                                        class="w-full text-xs px-3.5 py-2.5 border border-slate-250 dark:border-slate-700/80 rounded-xl bg-white dark:bg-slate-855 text-slate-800 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all font-bold">
                                    <option value="graded">Calificado (Bloquear)</option>
                                    <option value="pending">Pendiente (Reabrir)</option>
                                </select>
                                <span class="text-[9px] text-slate-400 block mt-1 font-semibold">
                                    {{ activeEvaluationRecord.status === 'pending' ? 'Permite corregir/reenviar.' : 'Entrega cerrada.' }}
                                </span>
                            </div>

                            <!-- Retroalimentación -->
                            <div class="md:col-span-2 space-y-1.5">
                                <label class="block text-[10px] font-extrabold uppercase tracking-wider text-slate-455 mb-1">Comentario de Retroalimentación (Feedback)</label>
                                <textarea v-model="activeEvaluationRecord.feedback" rows="3" placeholder="Muy buen trabajo, excelente resolución..."
                                          class="w-full text-xs px-3.5 py-2.5 border border-slate-250 dark:border-slate-700/80 rounded-xl bg-white dark:bg-slate-855 text-slate-800 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all resize-none"></textarea>
                            </div>
                        </div>

                    </div>

                    <!-- Footer -->
                    <div class="flex items-center justify-end gap-3 p-6 border-t border-slate-50 dark:border-slate-800/80 bg-slate-50/50 dark:bg-slate-950/20">
                        <button type="button" @click="showEvaluationModal = false"
                                class="px-4 py-2.5 border border-slate-200 dark:border-slate-700 text-slate-500 hover:bg-slate-105 dark:hover:bg-slate-800 rounded-xl text-xs font-bold transition-all">
                            Cancelar
                        </button>
                        <button type="button" @click="submitSingleGrade"
                                class="inline-flex items-center gap-1.5 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold shadow-md shadow-indigo-100 dark:shadow-none transition-all duration-200">
                            <Icon icon="tabler:circle-check" class="w-4.5 h-4.5" />
                            Guardar Calificación
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- MODAL: ALERT DIALOG (Custom Premium Alert Modal) -->
        <div v-if="showAlertModal" class="fixed inset-0 z-[100] flex items-center justify-center overflow-x-hidden overflow-y-auto outline-none focus:outline-none">
            <div class="fixed inset-0 bg-slate-900/60 dark:bg-slate-950/80 backdrop-blur-sm transition-opacity" @click="showAlertModal = false"></div>
            <div class="relative w-full max-w-sm mx-auto my-6 z-10 px-4">
                <div class="border border-slate-150 dark:border-slate-800 rounded-3xl shadow-2xl relative flex flex-col w-full bg-white dark:bg-slate-900 outline-none focus:outline-none overflow-hidden transition-all duration-300">
                    <div class="p-6 text-center space-y-4">
                        <!-- Icon based on title/type -->
                        <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full"
                             :class="[
                                 alertModalTitle.includes('no permitido') || alertModalTitle.includes('Error')
                                     ? 'bg-rose-50 dark:bg-rose-950/40 text-rose-500'
                                     : 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-500'
                             ]">
                            <Icon :icon="alertModalTitle.includes('no permitido') || alertModalTitle.includes('Error') ? 'tabler:alert-triangle' : 'tabler:circle-check'" class="w-6 h-6" />
                        </div>
                        
                        <div class="space-y-1">
                            <h3 class="text-sm font-extrabold text-slate-850 dark:text-white uppercase tracking-wider">
                                {{ alertModalTitle }}
                            </h3>
                            <p class="text-xs text-slate-500 dark:text-slate-450 leading-relaxed pt-1">
                                {{ alertModalMessage }}
                            </p>
                        </div>
                        
                        <div class="pt-2">
                            <button type="button" @click="showAlertModal = false"
                                    :class="[
                                        alertModalTitle.includes('no permitido') || alertModalTitle.includes('Error')
                                            ? 'bg-rose-600 hover:bg-rose-700 shadow-rose-100'
                                            : 'bg-indigo-600 hover:bg-indigo-700 shadow-indigo-100'
                                    ]"
                                    class="w-full inline-flex justify-center px-4 py-2 text-white font-bold rounded-xl text-xs transition-all shadow-md dark:shadow-none duration-150">
                                Entendido
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

    </BreezeAuthenticatedLayout>
</template>

<style scoped>
/* Styling placeholders to look clearly distinguished from typed text */
input::placeholder,
textarea::placeholder {
    opacity: 0.45 !important;
    font-style: italic !important;
    font-weight: 450 !important;
}

/* Custom styled thin scrollbars for code boxes & tables */
.custom-scrollbar::-webkit-scrollbar {
    width: 6px;
    height: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
}
.dark .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #475569;
}
</style>
